/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * E-Separation service layer (v2 — `esep/` contract).
 *
 * Wraps the project's existing `Api` (axios) HTTP client:
 *  - bearer token is injected from localStorage by `Api.headers`
 *  - requests/responses use the project `{ Header, Request }` envelope
 *  - 401 triggers the global auto-logout
 *
 * This file mirrors the mobile app's `esep/` v2 endpoints
 * (DMRC APP/.../src/services/eSeparation.service.ts) so the web speaks the
 * exact same API the backend now serves:
 *  - single action-driven approval endpoint (`esep/approval/action`)
 *  - HR-dispatched, configurable No-Dues departments
 *    (`esep/no-dues/dispatch` -> `dispatch-list` -> `dept-inbox` -> `dept-action`)
 *
 * Every read keeps a GRACEFUL FALLBACK to mock/empty data so the UI never
 * crashes when the backend is unreachable; mutations surface the real result.
 *
 * Legacy v1-only flows (Bond Waiver, manual vigilance/finance submit, document
 * PDF generation, prepone/postpone) are not part of v2 and are stubbed so the
 * existing pages keep importing/compiling cleanly.
 */
import axios from "axios";
import Api from "../../../../core/data/redux/utils/apiCall";
import { BASE_URLS } from "../../../../core/data/redux/apis/urls";
import { tokenService } from "../../../../core/utils/tokenService";

const BASE_URL = BASE_URLS.BASE_URL;

/* ───────────────────────────── Types ───────────────────────────── */
export interface EmployeeDetails {
  emp_name: string;
  emp_no: string;
  designation: string;
  emp_type: string; // Regular / Contract
  posting: string;       // Department
  wing?: string;         // Wing
  place_of_posting?: string; // Place of Posting
  payroll_area?: string; // Payroll Area
  customer_specific_status?: string; // Customer Specific Status
  emp_group?: string;    // Employee Group (A / B)
  pay_scale: string;
  doj: string;
  home_address: string;
  mobile: string;
  email: string;
  controlling_officer: string;
  co_emp_no: string;
  basic_pay_daily_rate: number;
  // SAP gender code: "1" = Male, "2" = Female (matches the convention
  // already used elsewhere in the app, e.g. myProfile.tsx's family-member
  // gender display). Gates the CCL section to female employees only.
  gender?: string;
}

export interface ResignationRequest {
  request_id?: string;
  emp_no: string;
  intimation_date: string;
  notice_end_date: string;
  // Superseded by bond_selections (dynamic HR-managed bond-type multi-select) —
  // kept optional so any lingering reader doesn't break, but no longer set by
  // the resignation form.
  under_bond?: boolean;
  bond_period?: string;
  bond_amount?: number;
  training_cost?: number;
  bond_selections?: BondSelection[];
  mobile: string;
  email: string;
  home_address?: string;
  temp_address?: string;
  early_relieving: boolean;
  desired_relieve_date?: string;
  shortfall_days?: number;
  short_notice_amount?: number;
  reason: string;
  noc_available: boolean;
  education_qual?: string;
  // Superseded by the `loans`/`loan_sureties` arrays built directly in
  // buildPayload() (see DECLARED_LOAN_TYPES) — kept optional only for any
  // other caller of this interface still passing it.
  outstanding_loans?: string[];
  attachments?: string[];
  status?: string;
  workflow_status?: string;
}

export interface ShortNoticeRequest {
  short_notice_id?: string;
  orig_request_id: string;
  emp_no: string;
  desired_relieve_date: string;
  shortfall_days: number;
  short_notice_amount: number;
  under_bond: boolean;
  bond_period?: string;
  training_expenditure?: number;
  ccl_lwp_availed: boolean;
  ccl_lwp_details?: string;
  custody_items: string[];
  custody_additional_details?: string;
  temp_charge_officer_name?: string;
  temp_charge_officer_emp_no?: string;
  temp_charge_officer_designation?: string;
  temp_charge_officer_dept?: string;
  attachments?: string[];
  status?: string;
}

export interface WithdrawalRequest {
  resignation_request_id: string;
  reason: string;
}

export interface BondWaiverRequest {
  bond_waiver_id?: string;
  orig_request_id: string;
  emp_no: string;
  waiver_date: string;
  waiver_reason: string;
  waiver_type: "full" | "partial";
  requested_waiver_amount?: number;
  bond_period?: string;
  bond_amount?: number;
  training_expenditure?: number;
  service_completed_under_bond?: string;
  status?: string;
}

export interface NoDuesDepartment {
  dept_name: string;
  nodal_officer: string;
  status: "Cleared" | "Pending" | "Dues Found";
  remarks?: string;
  due_items?: string[];
  recovery_amount?: number;
  dispatch_id?: number;
}

export interface FinanceSummary {
  outstanding_loans: number;
  advances_pending: number;
  training_recoveries: number;
  short_notice_amount: number;
  total_recoverable: number;
}

export interface NoDuesCase {
  nd_case_id: string;
  emp_no: string;
  emp_name: string;
  case_type: string;
  status: string;
  total_departments: number;
  cleared_departments: number;
  departments: NoDuesDepartment[];
  finance_summary: FinanceSummary;
  // Dedicated NO_DUES request id (ND-…), used to fetch the consolidated
  // recovery-settlement snapshot for the tracker.
  nodues_request_id?: string;
  // The parent resignation / case id — what the certificate download needs.
  request_id?: string;
}

export interface FinalDocument {
  doc_type: string;
  doc_name: string;
  available: boolean;
  download_url?: string;
}

// A file the employee (or HR, on the employee's behalf) attached to a
// resignation/short-notice/withdrawal request — visible to the employee, every
// approver in the chain, and HR, since they all load the same request detail.
export interface EsepDocumentItem {
  id: number;
  request_id: string;
  doc_type: string;
  s3_key: string;
  file_name: string;
  // Clarification attachments: the name the employee gave the document and the
  // clarification it answers.
  doc_name?: string | null;
  clarification_id?: number | null;
  created_at?: string;
}

// One past deputation row (Application for Intimation of Resignation, field
// 31 — organization + duration), shown when deputation_availed = 'Y'.
export interface DeputationHistoryRecord {
  id: number;
  request_id: string;
  organization_name: string;
  duration: string | null;
  sort_order: number;
}

export interface FinalDetails {
  emp_no: string;
  emp_name: string;
  designation: string;
  department: string;
  doj: string;
  resignation_type: string;
  intimation_date: string;
  notice_end_date: string;
  approved_relieving_date: string;
  competent_authority: string;
  short_notice_applied: boolean;
  shortfall_days?: number;
  short_notice_deducted?: number;
  bond_status: string;
  waiver_status?: string;
  no_dues_status: string;
  total_recoverable: number;
  total_payable: number;
  net_settlement: number;
  documents: FinalDocument[];
  request_id?: string;
  nodues_request_id?: string | null;
  // Set once the HR Assistant has released the Relieving Order / Service
  // Certificate (and gave the office order number).
  office_order_no?: string | null;
  // Everything is approved and cleared, but the HR Assistant has not released yet.
  docs_pending_release?: boolean;
  // The resignation was withdrawn (the withdrawal was approved): no acceptance
  // letter or certificates — only the Withdrawal Order is issued.
  is_withdrawal?: boolean;
  withdrawal_date?: string;
  withdrawal_reason?: string;
}

export interface SeparationListItem {
  id: string;
  type: string;
  status: string;
  // Derived status of a No-Dues-only separation case (it has no approval chain);
  // undefined for every other request.
  display_status?: string;
  date: string;
  nextAction: string;
  emp_no?: string;
  relieve_date?: string;
  raw?: any;
}

export interface ApprovalStep {
  level: string;
  approver_name: string;
  approver_emp_no: string;
  status:
    | "Approved"
    | "Pending"
    | "Rejected"
    | "Clarification"
    | "Current"
    | "Skipped"
    | "Dispatched"
    | "DuesFound"
    | "Settled"
    | "Collected";
  remarks?: string;
  action_date?: string;
  // The raw ledger action (APPROVE / REJECT / CLARIFY / …) this row came from.
  action?: string;
}

export type ApproverRole =
  | "Deputy"
  | "CO"
  | "HOD"
  | "HR"
  | "CA"
  | "MD"
  | "Nodal"
  | "Vigilance"
  | "Finance";
export type ApprovalActionType = "Approve" | "Reject" | "SendBack" | "Clarification";
export type SeparationType =
  | "Intimation"
  | "ShortNotice"
  | "Withdrawal"
  | "BondWaiver"
  | "NoDues"
  | "Termination"
  | "Death"
  | "Retirement"
  | "Deputation"
  | "TransferOut";
export type NoDuesSeparationMode =
  | "Resignation"
  | "Termination"
  | "Death"
  | "Deputation"
  | "Retirement"
  | "Transfer-Out";

export interface PendingApprovalItem {
  request_id: string;
  type: SeparationType;
  emp_name: string;
  emp_no: string;
  designation: string;
  department: string;
  submitted_date: string;
  days_pending: number;
  current_level: string;
  priority: "Normal" | "Urgent" | "Overdue";
  status: string;
  my_action?: string;
  request_type?: string;
  // True when the current step for this request (the one that's actually on
  // the caller's desk) is an HR-level step — any of V2's 4 levels, or V1's
  // single HR step. Used to route to the HR review screen instead of the
  // generic approver screen, regardless of which inbox list the caller used.
  current_step_is_hr?: boolean;
  current_step_role?: string;
  // True for a V2 Competent Authority item — these come from the parallel
  // esep_ca_approval track (esep/ca/inbox), not a step in the sequential
  // chain, so they route to the CA-specific detail action instead of the
  // regular per-step approval endpoint.
  is_ca_item?: boolean;
}

export interface ApprovalAction {
  request_id: string;
  action: ApprovalActionType;
  remarks: string;
  approver_role: ApproverRole;
  approver_emp_no: string;
  modified_relieving_date?: string;
}

export interface DARRemarks {
  level: string;
  officer_name: string;
  officer_emp_no: string;
  remarks: string;
  dar_status: "Cleared" | "Pending" | "Hold";
  action_date: string;
}

// D&AR (Disciplinary & Administrative Records) details stored per-request.
// Filled by AM / DY_HOD approvers; read-only for HR and above.
export interface DarDetails {
  id?: number;
  request_id?: string;
  dar_case_pending: "Y" | "N";
  dar_case_no?: string | null;
  dar_status?: string | null; // CLEARED | PENDING | HOLD | NOT_APPLICABLE
  dar_remarks?: string | null;
  filled_by_emp_id?: string;
  filled_by_role?: string | null;
  filled_at?: string | null;
}

// Real D&AR module case records (separate system of record from DarDetails
// above) — shown read-only to every approver and HR. Closed cases arrive
// pre-filtered to guilty-only from the backend.
export interface DarContemplatedCase {
  caseno: string;
  empno: string;
  casetype?: string | null;
  casedescription?: string | null;
  daname?: string | null;
  dadesignation?: string | null;
  status: string;
  createddate?: string;
}

export interface DarPendingCase {
  id: number;
  caseno: string;
  empno: string;
  daname?: string | null;
  dadesignation?: string | null;
  chargesheetdate?: string | null;
  charge_type?: string | null;
  remarks?: string | null;
  status: string;
}

export interface DarSuspensionCase {
  id: number;
  suspension_no?: string | null;
  caseno: string;
  empno: string;
  susp_type?: string | null;
  susp_start_date: string;
  susp_end_date?: string | null;
  status: string;
}

export interface DarClosedCase {
  id: number;
  caseno: string;
  empno: string;
  guilty_flag: boolean;
  exonerated_flag: boolean;
  penalty_type?: string | null;
  penalty_desc?: string | null;
  penalty_start_date?: string | null;
  penalty_end_date?: string | null;
  status: string;
}

export interface DarModuleCases {
  contemplated: DarContemplatedCase[];
  pending: DarPendingCase[];
  suspensions: DarSuspensionCase[];
  closed: DarClosedCase[];
}

/* "Send to Vigilance": HR dispatches a resignation to the configured single
   global vigilance officer, who clears/holds/objects via a generic form.
   Informational only — never blocks the approval chain. */
/* The nodal may delegate a case to one of their sub-nodals:
   PENDING (nodal) -> DELEGATED (sub-nodal) -> RETURNED (back with the nodal, who
   reviews the sub-nodal's findings) -> CLEARED / HOLD / OBJECTION (sent to HR). */
export type VigilanceCaseStatus =
  | "PENDING"
  | "DELEGATED"
  | "RETURNED"
  | "CLEARED"
  | "HOLD"
  | "OBJECTION";

/** The outcomes a vigilance form can record. */
export type VigilanceDecision = "CLEARED" | "HOLD" | "OBJECTION";

export interface VigilanceOfficerConfig {
  officer_emp_id: string | null;
}

export interface VigilanceCaseRecord {
  id: number;
  request_id: string;
  officer_emp_id: string;
  status: VigilanceCaseStatus;
  remarks: string | null;
  pending_case_ref: string | null;
  dispatched_by: string;
  dispatched_at: string | null;
  acted_at: string | null;
  // Sub-nodal delegation (kept after the case returns, as the record of who handled it).
  delegated_to_emp_id?: string | null;
  delegated_to_name?: string | null;
  delegated_by_emp_id?: string | null;
  delegated_at?: string | null;
  // What the sub-nodal recorded — a recommendation to the nodal, who sends the final outcome.
  sub_nodal_status?: VigilanceDecision | null;
  sub_nodal_remarks?: string | null;
  sub_nodal_pending_case_ref?: string | null;
  sub_nodal_acted_at?: string | null;
  emp_name?: string | null;
  emp_no?: string | null;
}

// The vigilance nodal's own registered sub-delegate.
export interface VigilanceSubNodal {
  id: number;
  created_by_emp_id: string;
  sub_nodal_emp_id: string;
  sub_nodal_name: string | null;
  is_active: boolean;
}

export interface NoDuesChecklistItem {
  item_id: string;
  item_name: string;
  status: "Returned" | "Not Returned" | "Not Applicable" | "Pending";
  remarks?: string;
}

export interface NoDuesTask {
  task_id: string;
  nd_case_id: string;
  dept_name: string;
  emp_name: string;
  emp_no: string;
  designation: string;
  department: string;
  case_type: string;
  assigned_date: string;
  status: "Pending" | "Cleared" | "Dues Found";
  checklist: NoDuesChecklistItem[];
  recovery_amount: number;
  remarks: string;
}

export interface FinanceLoanItem {
  loan_type: string;
  loan_id?: string;
  sanctioned_amount: number;
  outstanding_amount: number;
  recovery_required: number;
  status: "Active" | "Cleared" | "Written Off";
  remarks?: string;
}

export interface NoDuesInitiateData {
  emp_no: string;
  separation_mode: NoDuesSeparationMode;
  date_of_leaving: string;
  reason_for_leaving: string;
  initiated_by_emp_no: string;
}

export interface ProfileSection {
  fields: Record<string, string>;
}

export interface EmployeeProfile {
  emp_no: string;
  emp_name: string;
  father_name: string;
  mother_name: string;
  dob: string;
  gender: string;
  marital_status: string;
  blood_group: string;
  category: string;
  nationality: string;
  religion: string;
  pwd_status: string;
  mobile: string;
  email: string;
  permanent_address: string;
  present_address: string;
  photo_url?: string;
  designation: string;
  department: string;
  pay_scale: string;
  doj: string;
  status_of_appointment: string;
  emp_type: string;
  qualifications: ProfileSection[];
  experience_before_dmrc: ProfileSection[];
  experience_dmrc_grade: ProfileSection[];
  experience_dmrc_division: ProfileSection[];
  training: ProfileSection[];
  penalties: ProfileSection[];
  positions_outside: ProfileSection[];
  awards: ProfileSection[];
}

/* Bond types: HR-managed list + HR-defined dynamic fields per type. Replaces
   the old single "Under Bond?" Y/N + free-text bond_details on the resignation
   form — the employee now multi-selects which bond types apply to them and
   fills in each type's fields. */
export type BondFieldType = "TEXT" | "NUMBER" | "DATE" | "BOOLEAN" | "TEXTAREA";

export interface BondTypeField {
  id?: number;
  field_key: string;
  field_label: string;
  field_type: BondFieldType;
  is_required?: boolean;
  sort_order?: number;
}

export interface BondType {
  id: number;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  fields: BondTypeField[];
}

// One bond type the employee selected + the values they filled for its fields.
export interface BondSelection {
  bond_type_id: number;
  values: Record<string, any>;
}

// A selection as returned by find-one (values already saved, with a snapshot
// of the field definitions at submission time for stable rendering).
export interface BondSelectionRecord {
  id: number;
  bond_type_id: number;
  bond_type_name: string;
  field_meta: Array<{
    field_key: string;
    field_label: string;
    field_type: BondFieldType;
    is_required: boolean;
  }> | null;
  values: Record<string, any> | null;
  sort_order: number;
}

/* No-Dues v2: configurable departments + HR dispatch + dept clearance. */
export interface NoDuesDepartmentConfig {
  id: number;
  code: string;
  name: string;
  approver_emp_id: string | null;
  is_active: boolean;
  is_settlement: boolean;
  sort_order: number;
}

/* ─── No-Dues: consolidated recovery settlement (special settlement dept) ─── */
export interface NoDuesSettlement {
  id: number;
  request_id: string;
  department_id: number;
  department_name: string;
  approver_emp_id: string | null;
  status: "IN_PROGRESS" | "CLOSED";
  total_due: number;
  total_collected: number;
  comments: string | null;
  dispatched_at?: string;
  closed_at?: string | null;
}

// One department-reported due rolled into the consolidated settlement.
export interface NoDuesSettlementItem {
  id: number;
  settlement_id: number;
  request_id: string;
  source_department_id: number;
  source_department_name: string;
  item_name: string;
  amount_due: number;
  collected: boolean;
  collected_amount: number;
  remarks: string | null;
  acted_at?: string | null;
}

// HR / employee readiness probe + live settlement snapshot for a No-Dues request.
export interface NoDuesSettlementStatus {
  all_acted: boolean;
  total_due: number;
  has_settlement_dept: boolean;
  settlement: NoDuesSettlement | null;
  items: NoDuesSettlementItem[];
}

/* ───────── Finance recovery flow (recovery heads → managers → nodal) ───────── */
export interface FinanceItem {
  id: number;
  sn: number;
  item_name: string;
  amount_due: number;
  comments: string | null;
}
export interface FinanceTask {
  id: number;
  group_name: string;
  stage: number;
  status: "LOCKED" | "PENDING" | "SUBMITTED";
  assignee_emp_id: string;
  assignee_name: string | null;
  submitted_at: string | null;
  can_submit: boolean;
  total: number;
  items: FinanceItem[];
}
export interface FinanceReview {
  id: number;
  manager_emp_id: string;
  manager_name: string | null;
  label: string;
  status: "PENDING" | "VERIFIED";
  comments: string | null;
  acted_at: string | null;
  can_verify: boolean;
}
export interface FinanceView {
  has_flow: boolean;
  roles?: { nodal: boolean; recovery_head: boolean; manager: boolean };
  progress?: {
    tasks_submitted: number;
    tasks_total: number;
    reviews_verified: number;
    reviews_total: number;
  };
  all_tasks_submitted?: boolean;
  all_verified?: boolean;
  // Nodal / recovery head / manager may correct amounts until the settlement closes.
  can_edit_amounts?: boolean;
  tasks?: FinanceTask[];
  reviews?: FinanceReview[];
  signoff?: {
    vetted_verified: boolean;
    amount_received: boolean;
    nodal_comments: string | null;
    nodal_forwarded_at: string | null;
  };
  can_forward?: boolean;
}
export interface FinanceAssignment {
  group_id: number;
  code: string;
  name: string;
  stage: number;
  items?: { sn: number; name: string }[];
  assignees: {
    id: number;
    wing_scope: "ALL" | "OM" | "PROJ";
    emp_id: string;
    name: string | null;
    pending_count: number;
  }[];
}
export interface FinancePendingTask {
  id: number;
  request_id: string;
  emp_no: string | null;
  emp_name: string | null;
  group_name: string;
  stage: number;
  status: "LOCKED" | "PENDING";
  assignee_emp_id: string;
  assignee_name: string | null;
}
export interface FinanceMyWork {
  as_head: {
    request_id: string;
    emp_no: string | null;
    emp_name: string | null;
    designation: string | null;
    tasks: { id: number; group_name: string; status: string }[];
  }[];
  as_manager: {
    request_id: string;
    emp_no: string | null;
    emp_name: string | null;
    designation: string | null;
    label: string;
    open: boolean;
  }[];
}

export const submitFinanceTask = (data: {
  task_id: number;
  items: { item_id: number; amount_due: number; comments?: string }[];
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.financeTaskSubmit, data));

export const verifyFinanceRequest = (data: {
  request_id: string;
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.financeVerify, data));

export const forwardFinanceToHr = (data: {
  request_id: string;
  vetted_verified: boolean;
  amount_received: boolean;
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.financeForward, data));

// Any Finance member corrects amounts (recovery checklist, department-recorded dues,
// or the short-notice line).
export const editFinanceAmounts = (data: {
  kind: "CHECKLIST" | "DEPARTMENT" | "SHORT_NOTICE";
  entries: { item_id: number; amount_due: number; comments?: string }[];
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.financeAmountEdit, data));

export const transferFinanceTasks = (data: {
  task_ids: number[];
  to_emp_id: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.financeTransfer, data));

export const changeFinanceAssignee = (data: {
  assignee_id: number;
  new_emp_id: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.financeAssigneeChange, data));

export const getFinanceAssignments = (): Promise<ApiResult<FinanceAssignment[]>> =>
  withFallback(
    () => Api.post(ESEP.financeAssignments, {}),
    [],
    (p) => extractList({ data: p }) as FinanceAssignment[]
  );

export const getFinancePendingTasks = (): Promise<ApiResult<FinancePendingTask[]>> =>
  withFallback(
    () => Api.post(ESEP.financePendingTasks, {}),
    [],
    (p) => extractList({ data: p }) as FinancePendingTask[]
  );

export const getFinanceMyWork = (): Promise<ApiResult<FinanceMyWork>> =>
  withFallback(
    () => Api.post(ESEP.financeMyWork, {}),
    { as_head: [], as_manager: [] },
    (p) => unwrapDeep({ data: p }) as FinanceMyWork
  );

// Full settlement detail: header, per-department items, employee identity.
// One clearing department's No-Dues outcome, shown to the settlement department
// (Establishment / Finance) whether or not that department found any dues.
export interface NoDuesSettlementDepartment {
  id: number;
  department_id: number;
  department_name: string;
  approver_emp_id: string | null;
  approver_name: string | null;
  delegated_to_emp_id: string | null;
  status: "PENDING" | "CLEARED" | "REJECTED";
  remarks: string | null;
  recovery_amount: number | null;
  dispatched_at: string | null;
  acted_at: string | null;
  items: { id: number; item_name: string; amount_due: number; comments: string | null }[];
}

export interface NoDuesSettlementDetail {
  settlement: NoDuesSettlement | null;
  items: NoDuesSettlementItem[];
  // Every department's outcome — cleared ones too.
  departments?: NoDuesSettlementDepartment[];
  // The recovery-head / manager / nodal flow (has_flow=false for older settlements).
  finance?: FinanceView;
  request: {
    emp_no: string;
    emp_name: string | null;
    designation: string | null;
    orig_request_id: string | null;
    deputation_availed: string | null;
    deputation_remittance_received: string | null;
  } | null;
}

export interface NoDuesDispatchRow {
  id: number;
  request_id: string;
  department_id: number;
  department_code: string;
  department_name: string;
  approver_emp_id: string | null;
  status: "PENDING" | "CLEARED" | "REJECTED";
  remarks: string | null;
  recovery_amount: number | null;
  dispatched_at?: string;
  acted_at?: string;
  // Sub-Nodal delegation — set once the Nodal Officer hands this row off.
  delegated_to_emp_id?: string | null;
  delegated_to_name?: string | null;
  delegated_by_emp_id?: string | null;
  delegated_at?: string | null;
}

// A Nodal Officer's own registered sub-delegate within one department.
export interface NoDuesSubNodal {
  id: number;
  department_id: number;
  department_code: string;
  department_name: string;
  created_by_emp_id: string;
  sub_nodal_emp_id: string;
  sub_nodal_name: string | null;
  is_active: boolean;
}

export interface HrWingConfig {
  wing: string;
  hr_approver_emp_id: string;
}

// V2: the two HR level rosters. An employee's roster is chosen from their WING and
// GROUP together (resolveHrRoster below):
//   OM_NON_EXEC  – O&M (OPS / MNT) wing, non-executive
//   OM_EXEC_PROJ – O&M executive, Project executive and Project non-executive
export type EsepHrRoster = "OM_NON_EXEC" | "OM_EXEC_PROJ";

// V2: one row of the 4-level HR roster (HR Assistant / AM-Manager / Dy. HOD / HOD
// HR), keyed by (roster, level_no).
export interface EsepHrLevelConfig {
  roster: EsepHrRoster;
  level_no: 1 | 2 | 3 | 4;
  role: "HR_SR_CRA" | "HR_AM_MANAGER" | "HR_DY_HOD" | "HR_HOD";
  approver_emp_id: string;
}

// V2: the parallel Competent Authority approval for one request (null for V1
// requests, or a V2 request whose chain hasn't been built yet).
export interface EsepCaApproval {
  request_id: string;
  ca_role: "HOD" | "DIRECTOR" | "MD";
  approver_emp_id: string;
  approver_name?: string | null;
  // Set when the HOD HR sent it to someone other than the configured CA.
  original_approver_emp_id?: string | null;
  original_approver_name?: string | null;
  status: "NOT_SENT" | "PENDING" | "APPROVED" | "REJECTED";
  sent_by_emp_id: string | null;
  sent_at: string | null;
  acted_at: string | null;
  remarks: string | null;
}

// Executive = "A", Non-Executive = "B" — confirmed against real SAP-synced
// data (employee_details.employeeGroupText = 'Executive' only occurs where
// empGroup = 'A'; Group A is also the much smaller population). Centralizes
// the emp_group check that was previously duplicated raw across several
// screens — those raw checks previously had "B" for Executive, which was
// backwards.
export const isExecutiveEmpGroup = (empGroup: string | null | undefined): boolean =>
  String(empGroup ?? "").trim().toUpperCase() === "A";

// Mirrors the backend's resolveHrRoster: only the O&M non-executive population has
// a roster of its own; every Project employee and every O&M executive shares the
// other. Any wing that is not Project (MNT, OPS, or an unmapped wing) counts as O&M.
export const resolveHrRoster = (
  wing: string | null | undefined,
  empGroup: string | null | undefined
): EsepHrRoster | null => {
  const g = String(empGroup ?? "").trim().toUpperCase();
  if (g !== "A" && g !== "B") return null;
  const isProject = String(wing ?? "").trim().toUpperCase().startsWith("PROJ");
  return isProject || g === "A" ? "OM_EXEC_PROJ" : "OM_NON_EXEC";
};

export interface NoDuesDeptChecklistItem {
  id?: number;
  item_name: string;
  amount_due: number;
  comments?: string | null;
}

export interface NoDuesSavedItem {
  id: number;
  department_id: number;
  approver_emp_id: string;
  item_name: string;
  amount_due: number;
  comments: string | null;
}

export interface NoDuesDeptDetail {
  dispatch: NoDuesDispatchRow;
  form: Record<string, any> | null;
  checklist_items: NoDuesDeptChecklistItem[];
  saved_items: NoDuesSavedItem[];
}

export interface ApiResult<T> {
  success: boolean;
  data?: T;
  message?: string;
}


export const ESEP = {
  submit: BASE_URL + "esep/request/submit",
  resubmit: BASE_URL + "esep/request/resubmit",
  prefill: BASE_URL + "esep/request/prefill",
  hrUpdateRequest: BASE_URL + "esep/request/hr-update",
  findAll: BASE_URL + "esep/request/find-all",
  findOne: BASE_URL + "esep/request/find-one",
  upload: BASE_URL + "esep/request/upload",
  approval: BASE_URL + "esep/approval/action",
  clarificationRespond: BASE_URL + "esep/clarification/respond",
  shortNotice: BASE_URL + "esep/short-notice/submit",
  withdrawal: BASE_URL + "esep/withdrawal/submit",
  // HR-initiated separation cases (Termination / Death / ...).
  hrInitiate: BASE_URL + "esep/hr-initiate/submit",
  hrInitiateList: BASE_URL + "esep/hr-initiate/list",
  // Auto-captured SAP outstanding loans for the concerned employee.
  outstandingLoans: BASE_URL + "esep/request/outstanding-loans",
  noDuesSubmit: BASE_URL + "esep/no-dues/submit",
  noDuesInitiateCase: BASE_URL + "esep/no-dues/initiate-case",
  noDuesFindOne: BASE_URL + "esep/no-dues/find-one",
  noDuesDispatch: BASE_URL + "esep/no-dues/dispatch",
  noDuesDispatchAll: BASE_URL + "esep/no-dues/dispatch-all",
  noDuesDispatchList: BASE_URL + "esep/no-dues/dispatch-list",
  noDuesDeptAction: BASE_URL + "esep/no-dues/dept-action",
  noDuesDeptInbox: BASE_URL + "esep/no-dues/dept-inbox",
  noDuesDeptProcessed: BASE_URL + "esep/no-dues/dept-processed",
  noDuesDeptDetail: BASE_URL + "esep/no-dues/dept-detail",
  hrFinalReviewList: BASE_URL + "esep/request/hr-final-list",
  noDuesSavedItemsList: BASE_URL + "esep/no-dues/saved-items/list",
  noDuesSavedItemsSave: BASE_URL + "esep/no-dues/saved-items/save",
  noDuesSavedItemsDelete: BASE_URL + "esep/no-dues/saved-items/delete",
  // Consolidated recovery settlement (special settlement department).
  noDuesSettlementStatus: BASE_URL + "esep/no-dues/settlement/status",
  noDuesSettlementSend: BASE_URL + "esep/no-dues/settlement/send",
  noDuesSettlementInbox: BASE_URL + "esep/no-dues/settlement/inbox",
  noDuesSettlementProcessed: BASE_URL + "esep/no-dues/settlement/processed",
  noDuesSettlementDetail: BASE_URL + "esep/no-dues/settlement/detail",
  noDuesSettlementSettleItem: BASE_URL + "esep/no-dues/settlement/settle-item",
  noDuesSettlementClose: BASE_URL + "esep/no-dues/settlement/close",
  noDuesSettlementReportPdf: BASE_URL + "esep/no-dues/settlement/report/pdf",
  // Finance recovery flow (recovery heads -> Establishment managers -> nodal).
  financeTaskSubmit: BASE_URL + "esep/no-dues/settlement/finance/task-submit",
  financeVerify: BASE_URL + "esep/no-dues/settlement/finance/verify",
  financeForward: BASE_URL + "esep/no-dues/settlement/finance/forward",
  financeAmountEdit: BASE_URL + "esep/no-dues/settlement/finance/amount-edit",
  financeTransfer: BASE_URL + "esep/no-dues/settlement/finance/transfer",
  financeAssigneeChange: BASE_URL + "esep/no-dues/settlement/finance/assignee-change",
  financeAssignments: BASE_URL + "esep/no-dues/settlement/finance/assignments",
  financePendingTasks: BASE_URL + "esep/no-dues/settlement/finance/pending-tasks",
  financeMyWork: BASE_URL + "esep/no-dues/settlement/finance/my-work",
  adminHrConfig: BASE_URL + "esep/admin/hr-config",
  adminHrConfigList: BASE_URL + "esep/admin/hr-config/list",
  // V2: 4-level HR roster (Sr. CRA / AM-Manager / Dy. HOD / HOD HR), per group.
  adminHrLevelConfig: BASE_URL + "esep/admin/hr-level-config",
  adminHrLevelConfigList: BASE_URL + "esep/admin/hr-level-config/list",
  // V2: the parallel Competent Authority track.
  caSend: BASE_URL + "esep/ca/send",
  caAction: BASE_URL + "esep/ca/action",
  caInbox: BASE_URL + "esep/ca/inbox",
  caDetail: BASE_URL + "esep/ca/detail",
  adminNoDuesDeptList: BASE_URL + "esep/admin/no-dues-dept/list",
  adminNoDuesDept: BASE_URL + "esep/admin/no-dues-dept",
  adminNoDuesDeptDelete: BASE_URL + "esep/admin/no-dues-dept/delete",
  adminBondTypeList: BASE_URL + "esep/admin/bond-type/list",
  adminBondType: BASE_URL + "esep/admin/bond-type",
  adminBondTypeDelete: BASE_URL + "esep/admin/bond-type/delete",
  adminVigilanceConfig: BASE_URL + "esep/admin/vigilance-config",
  adminVigilanceConfigSave: BASE_URL + "esep/admin/vigilance-config/save",
  vigilanceSend: BASE_URL + "esep/vigilance/send",
  vigilanceCase: BASE_URL + "esep/vigilance/case",
  vigilanceInbox: BASE_URL + "esep/vigilance/inbox",
  vigilanceProcessed: BASE_URL + "esep/vigilance/processed",
  vigilanceAction: BASE_URL + "esep/vigilance/action",
  vigilanceSubAction: BASE_URL + "esep/vigilance/sub-action",
  vigilanceSubNodalCreate: BASE_URL + "esep/vigilance/sub-nodal/create",
  vigilanceSubNodalList: BASE_URL + "esep/vigilance/sub-nodal/list",
  vigilanceSubNodalDelete: BASE_URL + "esep/vigilance/sub-nodal/delete",
  vigilanceDelegate: BASE_URL + "esep/vigilance/delegate",
  vigilanceRevoke: BASE_URL + "esep/vigilance/revoke",
  vigilanceDelegatedByMe: BASE_URL + "esep/vigilance/delegated-by-me",
  capabilities: BASE_URL + "esep/me/capabilities",
  pendingCount: BASE_URL + "esep/me/pending-count",
  // Sub-Nodal delegation.
  subNodalCreate: BASE_URL + "esep/no-dues/sub-nodal/create",
  subNodalList: BASE_URL + "esep/no-dues/sub-nodal/list",
  subNodalDelete: BASE_URL + "esep/no-dues/sub-nodal/delete",
  subNodalDelegate: BASE_URL + "esep/no-dues/sub-nodal/delegate",
  subNodalRevoke: BASE_URL + "esep/no-dues/sub-nodal/revoke",
  subNodalDelegatedByMe: BASE_URL + "esep/no-dues/sub-nodal/delegated-by-me",
  // Document downloads.
  generateServiceCertificatePdf: BASE_URL + "esep/request/generate-service-certificate-pdf",
  generateRelievingOrderPdf: BASE_URL + "esep/request/generate-relieving-order-pdf",
  releaseFinalDocs: BASE_URL + "esep/request/release-final-docs",
  updateFinalDocs: BASE_URL + "esep/request/update-final-docs",
  generateWithdrawalOrderPdf: BASE_URL + "esep/request/generate-withdrawal-order-pdf",
  generateNoDuesCertificatePdf: BASE_URL + "esep/request/generate-no-dues-certificate-pdf",
} as const;

/* Endpoints that are NOT part of esep/ v2 but remain shared with other modules. */
const EMPLOYEE_PROFILE = BASE_URL + "employee/profile";
// E-Separation v2 Profile / Biodata — returns the structured shape the
// "Employee Profile / Biodata" screen renders (sourced from real HR tables).
const ESEP_PROFILE = BASE_URL + "esep/me/profile";
const ESEP_BIODATA_PDF = BASE_URL + "esep/me/biodata/pdf";
// HR Admin biodata endpoints — look up, edit, or download any employee's biodata.
const ESEP_HR_BIODATA_PROFILE = BASE_URL + "esep/hr/biodata/profile";
const ESEP_HR_BIODATA_UPDATE = BASE_URL + "esep/hr/biodata/update";
const ESEP_HR_BIODATA_PDF = BASE_URL + "esep/hr/biodata/pdf";
// Immediate receipt confirmation for a submitted request (any request type,
// no status gate) — distinct from the HR-generated post-approval acceptance
// letter.
const ESEP_ACKNOWLEDGEMENT_PDF = BASE_URL + "esep/request/acknowledgement/pdf";
const ESEP_ACCEPTANCE_PDF = BASE_URL + "esep/request/generate-acceptance-pdf";
const AWS_UPLOAD = BASE_URL + "aws-bucket/upload-file";
const AWS_GET_PRESIGNED_URL = BASE_URL + "aws-bucket/get-presigned-url";
const EMPLOYEES_FIND = BASE_URL + "employees/find-all";
// The main-app profile endpoints expose the structured `addresses[]` array that
// the SAP `employee/profile` master record does not — used as the reliable
// source for the employee's home address (see myProfile page).
const EMPLOYEES_MY_PROFILE = BASE_URL + "employees/my-profile";
const EMPLOYEES_EMPLOYEE_PROFILE = BASE_URL + "employees/employee-profile";

const ESEP_DOC_TYPES = [
  "NOC",
  "QUALIFICATION",
  "BOND_TRANSFER",
  "GRATUITY_TRANSFER",
  "SN_ATTACHMENT",
] as const;

/** Maps any legacy/UI doc label to a v2 doc_type the backend accepts. */
const normalizeDocType = (t?: string): string => {
  const up = String(t ?? "").toUpperCase().replace(/[\s-]/g, "_");
  if ((ESEP_DOC_TYPES as readonly string[]).includes(up)) return up;
  if (up.includes("QUAL")) return "QUALIFICATION";
  if (up.includes("BOND")) return "BOND_TRANSFER";
  if (up.includes("GRATUITY")) return "GRATUITY_TRANSFER";
  if (up.includes("SN") || up.includes("SHORT")) return "SN_ATTACHMENT";
  return "NOC";
};

/** The single v2 approval endpoint accepts APPROVE | REJECT | SEND_BACK | CLARIFY. */
const normalizeAction = (
  action?: string
): "APPROVE" | "REJECT" | "SEND_BACK" | "CLARIFY" => {
  const a = String(action ?? "").toUpperCase().replace(/[\s-]/g, "_");
  if (a === "REJECT") return "REJECT";
  if (a === "SENDBACK" || a === "SEND_BACK" || a === "RETURN") return "SEND_BACK";
  if (a === "CLARIFICATION" || a === "CLARIFY" || a === "CLARIFICATION_PENDING")
    return "CLARIFY";
  return "APPROVE";
};

/**
 * Post to an esep endpoint that REQUIRES `action` inside `Request.data`.
 *
 * The shared Api wrapper (`generateBody`) hoists any top-level `action` key out
 * of the body and into `Header.action` (a convention other modules depend on).
 * For `esep/approval/action` the backend validates `action` in the request
 * body, so that hoisting makes it disappear and the API rejects the call with
 * "action must be one of APPROVE | REJECT | SEND_BACK | CLARIFY".
 *
 * We replicate the exact envelope `generateBody` builds, but keep `action`
 * inside `Request.data`, and send it raw so the wrapper doesn't strip it again.
 */
function postKeepingAction(
  route: string,
  data: Record<string, any>
): Promise<any> {
  let endpoint = route;
  try {
    endpoint = new URL(route).pathname;
  } catch {
    /* route is already relative */
  }
  const guid =
    typeof crypto !== "undefined" && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const envelope = {
    Header: {
      device_id: "WEB",
      device_token: "WEB",
      device_type: "WEB",
      guid,
      epoch: Date.now(),
      url: endpoint,
      service_request_id: endpoint,
      action: data.action,
    },
    Request: { data },
  };
  return Api.post(route, envelope, true);
}

/* ───────────────────────── Generic helpers ───────────────────────── */
/** Pull the meaningful payload out of the project's response envelope. */
function unwrap(res: any): any {
  if (res == null) return res;
  return res?.Response?.data ?? res?.data?.data ?? res?.data ?? res;
}

/** Drills through nested `{ data: {...} }` envelopes to the inner payload. */
function unwrapDeep(res: any): any {
  let current = res?.Response ?? res;
  for (let i = 0; i < 6; i++) {
    if (
      current &&
      typeof current === "object" &&
      !Array.isArray(current) &&
      "data" in current &&
      current.data !== null &&
      current.data !== undefined
    ) {
      current = current.data;
    } else {
      break;
    }
  }
  return current;
}

/**
 * find-one returns `{ request, history, steps }`; flatten into the legacy-flat
 * shape screens expect (request fields at top level + history/steps attached).
 */
function flattenDetail(res: any): any {
  const d = unwrapDeep(res);
  if (!d || typeof d !== "object") return null;
  if (d.request) {
    const req = {
      ...d.request,
      doj: formatDate(d.request.doj),
      intimation_date: formatDate(d.request.intimation_date),
      notice_end_date: formatDate(d.request.notice_end_date),
      desired_relieve_date: formatDate(d.request.desired_relieve_date),
      relieved_wef: formatDate(d.request.relieved_wef),
      approved_wef: formatDate(d.request.approved_wef),
    };
    return {
      ...req,
      history: d.history ?? [],
      steps: d.steps ?? [],
      outstanding_loans: d.outstanding_loans ?? [],
      clarifications: d.clarifications ?? [],
      bond_selections: d.bond_selections ?? d.request?.bond_selections ?? [],
      vigilance_case: d.vigilance_case ?? d.request?.vigilance_case ?? null,
      dar_details: d.dar_details ?? d.request?.dar_details ?? null,
      dar_module_cases: d.dar_module_cases ?? d.request?.dar_module_cases ?? null,
      documents: d.documents ?? d.request?.documents ?? [],
      deputation_history: d.deputation_history ?? d.request?.deputation_history ?? [],
      loan_sureties: d.loan_sureties ?? d.request?.loan_sureties ?? [],
      // V2 only (null for V1 requests) — the parallel Competent Authority
      // approval, and whether all 4 HR levels have approved (generalizes to
      // V1's single HR step too). workflow_version itself needs no explicit
      // entry here: it's a plain scalar column already carried through by the
      // `...d.request` spread above.
      ca_approval: d.ca_approval ?? d.request?.ca_approval ?? null,
      hr_chain_complete: d.hr_chain_complete ?? d.request?.hr_chain_complete ?? false,
      // Final release of the Relieving Order / Service Certificate (per viewer):
      // everything approved + cleared, and whether THIS viewer — the HR Assistant —
      // may release. (office_order_no / released_at are plain request columns,
      // already carried by the spread above.)
      final_docs_ready: d.final_docs_ready ?? false,
      can_release_final_docs: d.can_release_final_docs ?? false,
      // The HR Assistant may change the signing authority / office order number any time.
      can_manage_final_docs: d.can_manage_final_docs ?? false,
      signing_authority: d.signing_authority ?? null,
      // Derived No-Dues status of a No-Dues-only separation case (undefined for
      // every other request).
      display_status: d.display_status ?? d.request?.display_status ?? undefined,
    };
  }
  return d;
}

/** Walk through any nesting and return the first array we find. */
function extractList(res: any): any[] {
  let current = res?.Response ?? res;
  for (let i = 0; i < 6; i++) {
    if (Array.isArray(current)) {
      break;
    }
    if (
      current &&
      typeof current === "object" &&
      "data" in current &&
      current.data !== null &&
      current.data !== undefined
    ) {
      current = current.data;
    } else {
      break;
    }
  }
  if (Array.isArray(current)) {
    return current.map((item: any) => {
      if (item && typeof item === "object") {
        return {
          ...item,
          doj: formatDate(item.doj),
          intimation_date: formatDate(item.intimation_date),
          notice_end_date: formatDate(item.notice_end_date),
          desired_relieve_date: formatDate(item.desired_relieve_date),
          relieved_wef: formatDate(item.relieved_wef),
          approved_wef: formatDate(item.approved_wef),
          submitted_date: formatDate(item.submitted_date),
          acted_at: formatDate(item.acted_at),
          dispatched_at: formatDate(item.dispatched_at),
          created_at: formatDate(item.created_at),
          updated_at: formatDate(item.updated_at),
          closed_at: formatDate(item.closed_at),
          assigned_date: formatDate(item.assigned_date),
          date_of_joining: formatDate(item.date_of_joining),
          date_of_birth: formatDate(item.date_of_birth),
          dateOfJoining: formatDate(item.dateOfJoining),
          dateOfBirth: formatDate(item.dateOfBirth),
          date_of_leaving: formatDate(item.date_of_leaving),
          dateOfLeaving: formatDate(item.dateOfLeaving),
        };
      }
      return item;
    });
  }
  return [];
}

/**
 * Run a mutating API call (submit/approve) and surface the REAL result.
 * A server error here returns `success: false` with the backend message so the
 * UI does not falsely report success.
 */
async function withFallbackStrict(
  call: () => Promise<any>
): Promise<ApiResult<any>> {
  try {
    const res = await call();
    return { success: true, data: unwrap(res) };
  } catch (e: any) {
    return { success: false, message: Api.getErrorMessage(e) };
  }
}

/**
 * Run an API call and, on any error/empty payload, fall back to `fallback`.
 * Never throws so the UI keeps working offline.
 */
async function withFallback<T>(
  call: () => Promise<any>,
  fallback: T,
  pick?: (payload: any) => T | undefined
): Promise<ApiResult<T>> {
  try {
    const res = await call();
    const payload = unwrap(res);
    const data = (pick ? pick(payload) : (payload as T)) ?? fallback;
    return { success: true, data };
  } catch (e: any) {
    return { success: true, data: fallback, message: Api.getErrorMessage(e) };
  }
}

/* ─────────────────────────── Utility helpers ────────────────────── */
const DAY_MS = 1000 * 60 * 60 * 24;

export const calculateNoticeEndDate = (d: string | Date): Date => {
  const date = new Date(d);
  date.setDate(date.getDate() + 89);
  return date;
};

export const calculateShortfallDays = (
  noticeEnd: string | Date,
  desired: string | Date
): number => {
  const end = new Date(noticeEnd).getTime();
  const want = new Date(desired).getTime();
  return Math.max(0, Math.ceil((end - want) / DAY_MS));
};

export const calculateShortNoticeAmount = (
  dailyRate: number,
  shortfallDays: number
): number => Math.max(0, Math.round((dailyRate || 0) * (shortfallDays || 0)));

export const formatDate = (d?: string | Date | null): string => {
  if (!d) return "—";
  if (typeof d === "string") {
    const s = d.trim();
    if (!s) return "—";
    // Idempotent: already formatted as dd-mm-yyyy (avoids new Date("15-08-2026")
    // producing an Invalid Date on a second pass).
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;
    // Date-only ISO (YYYY-MM-DD) — format directly to avoid the UTC day-shift.
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return `${ymd[3]}-${ymd[2]}-${ymd[1]}`;
    // ISO datetime — use the stored calendar date portion (consistent with the
    // slice(0,10) approach used elsewhere) so timezone never shifts the day.
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  }
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Date + time formatter for the approval timeline. Shows a consistent local
// date + time. Tolerant of already-formatted dd-mm-yyyy input (returns it
// unchanged) and non-parseable values.
export const formatDateTime = (d?: string | Date | null): string => {
  if (!d) return "—";
  if (typeof d === "string" && /^\d{2}-\d{2}-\d{4}$/.test(d.trim())) {
    return d.trim();
  }
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return formatDate(d);
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = dt.getFullYear();
  const time = dt.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${day}-${month}-${year}, ${time}`;
};

export const formatDateLong = (d?: string | Date | null): string => {
  return formatDate(d);
};

export const formatINR = (value?: number | null): string =>
  `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

/** Convert a DD/MM/YYYY date string to YYYY-MM-DD. Pass-through if already YMD. */
export const convertDMYToYMD = (dateStr?: string): string => {
  if (!dateStr) return "";
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month}-${day}`;
  }
  return dateStr;
};

/** Current logged-in employee number from the stored user. */
export const getCurrentEmpNo = (): string => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.empId || user?.emp_no || user?.emp_id || "";
  } catch {
    return "";
  }
};

/* ─── Active-request gating helpers (mirror the mobile app) ─── */
export type SeparationKind = "RESIGNATION" | "SHORT_NOTICE" | "WITHDRAWAL";

const ESEP_TERMINAL_STATUSES = ["APPROVED", "REJECTED", "WITHDRAWN", "SUPERSEDED", "CLOSED"];

export const isActiveSeparation = (
  item: { status?: string } | null | undefined
): boolean => {
  const s = String(item?.status ?? "").toUpperCase();
  return s.length > 0 && !ESEP_TERMINAL_STATUSES.includes(s);
};

export const normalizeSeparationKind = (
  type?: string
): SeparationKind | null => {
  const up = String(type ?? "").toUpperCase().replace(/[\s-]/g, "_");
  if (up.includes("SHORT")) return "SHORT_NOTICE";
  if (up.includes("WITHDRAW")) return "WITHDRAWAL";
  if (up.includes("RESIGN") || up.includes("INTIMATION")) return "RESIGNATION";
  return null;
};

export const hasActiveSeparation = (
  requests: Array<{ type?: string; status?: string }>,
  kind: SeparationKind
): boolean =>
  (requests || []).some(
    (r) => normalizeSeparationKind(r.type) === kind && isActiveSeparation(r)
  );

// Statuses after which the employee may apply afresh. Everything else — including
// an APPROVED separation — blocks a new request of the same kind.
const ESEP_REAPPLIABLE_STATUSES = ["REJECTED", "WITHDRAWN"];

// True when a separation of the given kind exists that blocks a NEW one — i.e. it
// is either in process OR already approved (unlike hasActiveSeparation, which
// treats APPROVED as inactive). Use this to gate launching a fresh request.
export const hasBlockingSeparation = (
  requests: Array<{ type?: string; status?: string }>,
  kind: SeparationKind
): boolean =>
  (requests || []).some(
    (r) =>
      normalizeSeparationKind(r.type) === kind &&
      !ESEP_REAPPLIABLE_STATUSES.includes(String(r.status ?? "").toUpperCase())
  );

// Raw backend types that follow ONLY the No-Dues workflow (no resignation /
// approval flow). HR can initiate every one of them; the employee can initiate
// DEPUTATION, RETIREMENT and TRANSFER_OUT for themselves.
export const ESEP_NO_DUES_ONLY_TYPES = [
  "TERMINATION",
  "DEATH",
  "DEPUTATION",
  "RETIREMENT",
  "TRANSFER_OUT",
];
export const ESEP_EMPLOYEE_NO_DUES_TYPES = [
  "DEPUTATION",
  "RETIREMENT",
  "TRANSFER_OUT",
];

// True when a No-Dues-only separation case (HR- or employee-initiated) is live
// against the employee. While one exists the employee cannot raise any
// self-service separation request.
export const hasActiveHrInitiatedCase = (
  requests: Array<{ type?: string; request_type?: string; status?: string }>
): boolean =>
  (requests || []).some((r) => {
    const type = String(r.type ?? r.request_type ?? "").toUpperCase();
    return (
      ESEP_NO_DUES_ONLY_TYPES.includes(type) &&
      !ESEP_REAPPLIABLE_STATUSES.includes(String(r.status ?? "").toUpperCase())
    );
  });

/* ──────────────────────────── Mock data ─────────────────────────── */
const MOCK_EMPLOYEE: EmployeeDetails = {
  emp_name: "—",
  emp_no: getCurrentEmpNo(),
  designation: "—",
  emp_type: "Regular",
  posting: "—",
  wing: "—",
  place_of_posting: "—",
  payroll_area: "—",
  customer_specific_status: "—",
  emp_group: "—",
  pay_scale: "—",
  doj: "",
  home_address: "—",
  mobile: "",
  email: "",
  controlling_officer: "—",
  co_emp_no: "—",
  basic_pay_daily_rate: 0,
};

const ND_DEPARTMENTS = [
  "IT",
  "Admin",
  "Finance",
  "Stores",
  "Vigilance",
  "HR",
  "Library",
];

const emptyFinanceSummary: FinanceSummary = {
  outstanding_loans: 0,
  advances_pending: 0,
  training_recoveries: 0,
  short_notice_amount: 0,
  total_recoverable: 0,
};

/* ─── No-Dues mapping (v2 enablement row -> UI case) ─── */
export const mapV2NoDuesToUI = (nd: any, empNo?: string): NoDuesCase => {
  if (!nd) {
    return {
      nd_case_id: "",
      emp_no: empNo ?? "",
      emp_name: "",
      case_type: "Resignation",
      status: "Not Enabled",
      total_departments: 0,
      cleared_departments: 0,
      departments: [],
      finance_summary: { ...emptyFinanceSummary },
    };
  }

  const status = String(nd.status ?? "").toUpperCase();
  const uiStatus =
    status === "SUBMITTED"
      ? "Completed"
      : status === "ENABLED"
      ? "Pending"
      : "Not Enabled";

  const departmentsList: NoDuesDepartment[] = ND_DEPARTMENTS.map((d) => ({
    dept_name: d,
    nodal_officer: `${d} Officer`,
    status: uiStatus === "Completed" ? "Cleared" : "Pending",
    remarks: uiStatus === "Completed" ? "All dues cleared" : "",
    due_items: [],
    recovery_amount: 0,
  }));

  return {
    nd_case_id: String(nd.id ?? nd.request_id ?? ""),
    emp_no: nd.emp_no ?? empNo ?? "",
    emp_name: "",
    case_type: "Resignation",
    status: uiStatus,
    total_departments: ND_DEPARTMENTS.length,
    cleared_departments: uiStatus === "Completed" ? ND_DEPARTMENTS.length : 0,
    departments: departmentsList,
    finance_summary: { ...emptyFinanceSummary },
  };
};

/**
 * Deep-search a response object for the employee's home address.
 *
 * The employee/profile payload nests the address at different depths/casings
 * depending on the caller (root `presentAddress` camelCase in the biodata shape
 * vs `personal_details.present_address` snake_case in the master-data shape),
 * so a fixed key path misses it — the web deep-unwrap can land on a level that
 * has name/designation but not the address. We walk the whole object and return
 * the first non-empty address-like value, preferring present over permanent.
 */
const ADDRESS_KEY_PATTERNS = [
  /present.?address/i,
  /permanent.?address/i,
  /correspondence.?address/i,
  /current.?address/i,
  /home.?address/i,
  /residential.?address/i,
  /^address$/i,
];

// Ordered address part keys used by the main-app profile `addresses[]` objects.
const ADDRESS_PART_KEYS = [
  "careOf",
  "care_of",
  "house_number",
  "houseNumber",
  "streetHouse",
  "street",
  "address_line_1",
  "addressLine1",
  "address_line_2",
  "addressLine2",
  "locality",
  "landmark",
  "city",
  "district",
  "state",
  "postalCode",
  "postal_code",
  "pincode",
  "pinCode",
  "country",
];

// Build a single readable line from a structured address object.
const composeAddressObject = (obj: any): string => {
  if (!obj || typeof obj !== "object") return "";
  const parts = ADDRESS_PART_KEYS.map((k) => obj[k])
    .filter((v) => v !== undefined && v !== null && String(v).trim())
    .map((v) => String(v).trim());
  return Array.from(new Set(parts)).join(", ");
};

const composeFromAddressArray = (arr: any[]): string => {
  if (!Array.isArray(arr) || !arr.length) return "";
  const typeOf = (a: any) =>
    String(a?.addressType || a?.address_type || a?.type || "").toLowerCase();
  const preferred =
    arr.find((a) => /present|current|correspond|residence/.test(typeOf(a))) ||
    arr.find((a) => /permanent/.test(typeOf(a))) ||
    arr[0];
  const composed = composeAddressObject(preferred);
  if (composed) return composed;
  for (const a of arr) {
    const c = composeAddressObject(a);
    if (c) return c;
  }
  return "";
};

const findHomeAddress = (root: any): string => {
  if (!root || typeof root !== "object") return "";
  const found: Array<{ rank: number; value: string }> = [];
  let arrayComposed = "";
  const seen = new Set<any>();
  const visit = (node: any) => {
    if (!node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      // Structured address arrays (main-app profile shape) — compose a line.
      if (!arrayComposed && /address(es)?/i.test(key) && Array.isArray(value)) {
        arrayComposed = composeFromAddressArray(value);
      }
      if (typeof value === "string" && value.trim()) {
        const rank = ADDRESS_KEY_PATTERNS.findIndex((re) => re.test(key));
        if (rank !== -1) found.push({ rank, value: value.trim() });
      } else if (value && typeof value === "object") {
        visit(value);
      }
    }
  };
  visit(root);
  if (found.length) {
    found.sort((a, b) => a.rank - b.rank);
    return found[0].value;
  }
  return arrayComposed;
};

const fetchHomeAddressFallback = async (empNo: string): Promise<string> => {
  const isSelf =
    !empNo || String(empNo).trim() === String(getCurrentEmpNo()).trim();
  const attempts: Array<() => Promise<any>> = isSelf
    ? [
        () => Api.post(EMPLOYEES_MY_PROFILE, {}),
        () => Api.post(EMPLOYEES_EMPLOYEE_PROFILE, { id: empNo }),
      ]
    : [
        () => Api.post(EMPLOYEES_EMPLOYEE_PROFILE, { id: empNo }),
        () => Api.post(EMPLOYEES_EMPLOYEE_PROFILE, { empId: empNo }),
      ];
  for (const call of attempts) {
    try {
      const res = await call();
      const addr = findHomeAddress(res);
      if (addr) return addr;
    } catch {
      /* try next */
    }
  }
  return "";
};

// Deep-search a response object for the first scalar value whose key matches
// any of the supplied patterns (handles alternate casing / nesting / depth).
const findFieldDeep = (root: any, patterns: RegExp[]): string => {
  if (!root || typeof root !== "object") return "";
  const seen = new Set<any>();
  let result = "";
  const visit = (node: any) => {
    if (result || !node || typeof node !== "object" || seen.has(node)) return;
    seen.add(node);
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      if (result) return;
      if (
        (typeof value === "string" || typeof value === "number") &&
        String(value).trim() &&
        patterns.some((re) => re.test(key))
      ) {
        result = String(value).trim();
        return;
      }
      if (value && typeof value === "object") visit(value);
    }
  };
  visit(root);
  return result;
};

// Pull the my-profile record (same source the My Profile page uses) so the
// e-Separation forms can back-fill fields the SAP employee/profile call omits
// (place of posting, payroll area, customer-specific status, employee group,
// date of joining). Returns the raw response for deep-searching.
const fetchMyProfileRaw = async (empNo: string): Promise<any> => {
  const isSelf =
    !empNo || String(empNo).trim() === String(getCurrentEmpNo()).trim();
  const attempts: Array<() => Promise<any>> = isSelf
    ? [
        () => Api.post(EMPLOYEES_MY_PROFILE, {}),
        () => Api.post(EMPLOYEES_EMPLOYEE_PROFILE, { id: empNo }),
      ]
    : [
        () => Api.post(EMPLOYEES_EMPLOYEE_PROFILE, { id: empNo }),
        () => Api.post(EMPLOYEES_EMPLOYEE_PROFILE, { empId: empNo }),
      ];
  for (const call of attempts) {
    try {
      const res = await call();
      if (res) return res;
    } catch {
      /* try next */
    }
  }
  return null;
};

/* ─────────────────────────── API functions ──────────────────────── */
// Employee master (SAP) — sourced from employee/profile.
export const getEmployeeDetails = async (
  empNo?: string
): Promise<ApiResult<EmployeeDetails>> => {
  const emp_no = empNo || getCurrentEmpNo();
  const fallback: EmployeeDetails = { ...MOCK_EMPLOYEE, emp_no };
  try {
    const res = await Api.post(EMPLOYEE_PROFILE, { emp_no });
    const p = unwrap(res);
    const pd = p?.personal_details || p || {};
    // Address fields live at the response root in the real API (the same
    // place the Employee Profile page reads them), not under personal_details.
    const addr = p || {};
    const name =
      [pd.firstname, pd.secondname, pd.lsastname].filter(Boolean).join(" ").trim() ||
      pd.fullName ||
      pd.emp_name ||
      "";
    const data: EmployeeDetails = {
      emp_name: name,
      emp_no: p?.emp_no || pd.empId || pd.emp_no || emp_no,
      designation:
        pd.designation_name || pd.designation || pd.designationID || "",
      emp_type: pd.employmentType || pd.emp_type || "Regular",
      posting: pd.department_name || pd.department || pd.posting || "",
      wing: pd.wing || pd.wing_name || "",
      place_of_posting:
        pd.place_of_posting || pd.placeOfPosting || pd.placeofposting || "",
      payroll_area:
        pd.payroll_area || pd.payrollArea || pd.payroll || "",
      customer_specific_status:
        pd.customer_specific_status ||
        pd.customerSpecificStatus ||
        pd.customer_specific ||
        "",
      emp_group:
        pd.emp_group || pd.employeeGroup || pd.employee_group || pd.group || "",
      pay_scale: pd.pay_scale || pd.payscale || "",
      doj: pd.date_of_joining || pd.dateOfJoining || pd.doj || "",
      home_address:
        addr.present_address ||
        addr.permanent_address ||
        addr.presentAddress ||
        addr.permanentAddress ||
        pd.present_address ||
        pd.permanent_address ||
        pd.permanentAddress ||
        pd.presentAddress ||
        pd.home_address ||
        pd.homeAddress ||
        pd.correspondence_address ||
        pd.current_address ||
        pd.address ||
        // Deep-search the raw response for any address-like field or a
        // structured addresses[] array (handles alternate keys/casing/depth).
        findHomeAddress(res) ||
        "",
      mobile: pd.phone || pd.mobile || "",
      email: pd.email || "",
      controlling_officer: pd.controlling_officer || "Sanjay Kapoor",
      co_emp_no: pd.co_emp_no || "20018374",
      basic_pay_daily_rate:
        parseFloat(pd.basicSalary) || pd.basic_pay_daily_rate || 0,
      gender: pd.gender != null ? String(pd.gender) : "",
    };
    // The SAP master record frequently has no address — pull it from the
    // main-app profile (structured addresses[]) so the field is never blank.
    if (!data.home_address) {
      data.home_address = await fetchHomeAddressFallback(data.emp_no);
    }
    // The SAP employee/profile call often omits place of posting, employee
    // group and DOJ. Back-fill any that are still blank from the my-profile API
    // (same source as the My Profile page). Payroll Area and Customer Specific
    // Status are sourced from the resignation prefill API instead.
    if (!data.place_of_posting || !data.emp_group || !data.doj) {
      const mp = await fetchMyProfileRaw(data.emp_no);
      if (mp) {
        if (!data.place_of_posting)
          data.place_of_posting = findFieldDeep(mp, [
            /place[_-]?of[_-]?posting/i,
            /posting[_-]?place/i,
          ]);
        if (!data.emp_group)
          data.emp_group = findFieldDeep(mp, [
            /emp(loyee)?[_-]?group/i,
            /^group$/i,
          ]);
        if (!data.doj)
          data.doj = findFieldDeep(mp, [
            /date[_-]?of[_-]?joining/i,
            /^doj$/i,
          ]);
      }
    }
    return { success: true, data };
  } catch (e: any) {
    return { success: true, data: fallback, message: Api.getErrorMessage(e) };
  }
};

const toYN = (v: any): "Y" | "N" => (v ? "Y" : "N");

// Resignation / Intimation — submit (fresh) or resubmit (after SEND_BACK).
// Builds the exact v2 backend payload the NestJS controller expects.
export const submitResignation = (
  data: ResignationRequest & { request_id?: string; [k: string]: any }
): Promise<ApiResult<any>> => {
  const payload: Record<string, any> = {
    // Whether the employee is under a service bond period — a single
    // HR-configured bond type (dropdown) + Training Cost. Short Notice Period
    // Amount is always auto-calculated server-side, never sent from here.
    is_bond: toYN((data as any).is_bond),
    bond_type_id: (data as any).bond_type_id || undefined,
    training_bond_amount: (data as any).training_bond_amount || 0,
    // Notice end date is non-editable — always intimation + 89 days,
    // computed and enforced server-side. Sent only for display continuity.
    notice_end_date: convertDMYToYMD(data.notice_end_date) || undefined,
    mobile: data.mobile,
    email: data.email,
    temp_address: data.temp_address || "",
    early_relieve: toYN(data.early_relieving),
    desired_relieve_date: convertDMYToYMD(data.desired_relieve_date) || undefined,
    reason: data.reason || "",
    noc_attached: toYN(data.noc_available),
    // NOC sub-details (Post applied for / Advt. No. / Date) — only sent when
    // NOC itself is Yes.
    noc_post: data.noc_available ? (data as any).noc_post || "" : "",
    noc_advt_no: data.noc_available ? (data as any).noc_advt_no || "" : "",
    noc_date: data.noc_available ? convertDMYToYMD((data as any).noc_date) || undefined : undefined,
    qualification_details: data.education_qual || "",
    payable_amount: (data as any).payable_amount || 0,
    // surety_flag/surety_details are auto-fill only — the backend fetches
    // them from the real sap_loan_sureties data itself; not sent from here.
    items_in_custody: toYN(
      Array.isArray((data as any).items_in_custody)
        ? (data as any).items_in_custody.length
        : (data as any).items_in_custody
    ),
    items_in_custody_text: Array.isArray((data as any).items_in_custody)
      ? (data as any).items_in_custody.join(", ")
      : (data as any).items_in_custody_text || "",
    temp_charge_officer: (data as any).temp_charge_officer || "",
    // "Have you undergone any Foreign Training and signed the bond?" —
    // distinct from the general Service Bond section.
    foreign_training_bond: toYN((data as any).foreign_training_bond),
    foreign_training_period:
      (data as any).foreign_training_bond === true || (data as any).foreign_training_bond === "Y"
        ? (data as any).foreign_training_period || ""
        : "",
    foreign_training_bond_amount:
      Number((data as any).foreign_training_bond_amount) || 0,
    foreign_training_details:
      (data as any).foreign_training_bond === true || (data as any).foreign_training_bond === "Y"
        ? (data as any).foreign_training_details || ""
        : "",
    training_provided: toYN((data as any).training_provided),
    training_details: (data as any).training_details || "",
    other_bond_submitted: toYN((data as any).other_bond_submitted),
    other_bond_details: (data as any).other_bond_details || "",
    // CCL and LWP availed in the preceding two years — two separate fields.
    ccl_availed: toYN((data as any).ccl_availed),
    ccl_period: (data as any).ccl_period || "",
    lwp_availed: toYN((data as any).lwp_availed),
    lwp_period: (data as any).lwp_period || "",
    // "Extraordinary Leave (EOL) availed" — duration + exact dates.
    eol_availed: toYN((data as any).eol_availed),
    eol_details: (data as any).eol_availed ? (data as any).eol_details || "" : "",
    withdraw_last_yr: toYN((data as any).withdraw_last_yr),
    withdraw_last_yr_details: (data as any).withdraw_last_yr_details || "",
    // Deputation history declaration.
    deputation_availed: toYN((data as any).deputation_availed),
    deputation_remittance_received: (data as any).deputation_availed
      ? toYN((data as any).deputation_remittance_received)
      : undefined,
    deputation_history: (data as any).deputation_availed
      ? ((data as any).deputation_history || [])
          .filter((d: any) => (d?.organization_name ?? "").trim())
          .map((d: any) => ({
            organization_name: d.organization_name.trim(),
            duration: d.duration || "",
          }))
      : [],
    // Employee's own declaration of pending Court Case / Vigilance / D&AR.
    pending_cases_details: (data as any).pending_cases_details || "",
    // Service Bond to be transferred only applies when NOC is available —
    // enforced again server-side regardless of what's sent here.
    bond_transfer: toYN((data as any).noc_available) === "Y" ? toYN((data as any).bond_transfer) : "N",
    gratuity_transfer: toYN((data as any).gratuity_transfer),
    separation_type: (data as any).separation_type || "RESIGNATION",
    // "Indicate Loan or Advances" (field 25, employee-declared) + any
    // corrected amounts for auto-fetched SAP loans (each carries its own
    // reference_number) — built directly by the form now, not derived from
    // a checkbox list.
    loans: (data as any).loans || [],
    // "Your Sureties for Loans, if any" (field 26) — entirely local to
    // e-separation, never written back to sap_loan_sureties.
    loan_sureties: (data as any).loan_sureties || [],
    non_compete_declaration: toYN((data as any).non_compete_declaration),
  };

  if (data.request_id) {
    payload.request_id = data.request_id;
    return withFallbackStrict(() => Api.post(ESEP.resubmit, payload));
  }
  return withFallbackStrict(() => Api.post(ESEP.submit, payload));
};

// HR correction of any field on a resignation-style request (dates, reason,
// bond/custody/loan details, etc.) before it is forwarded to the Competent
// Authority. A partial update — pass only the fields being changed; every
// key maps 1:1 to a HrUpdateRequestDto field on the backend (see
// esep/request/hr-update). loans/bond_selections, if included, fully replace
// the existing rows.
export interface HrUpdateRequestFields {
  request_id: string;
  emp_name?: string;
  designation?: string;
  emp_type?: string;
  posting?: string;
  pay_scale?: string;
  doj?: string;
  hometown?: string;
  mobile?: string;
  email?: string;
  temp_address?: string;
  co_name?: string;
  co_mobile?: string;
  intimation_date?: string;
  notice_end_date?: string;
  early_relieve?: "Y" | "N";
  desired_relieve_date?: string;
  reason?: string;
  noc_attached?: "Y" | "N";
  noc_post?: string;
  noc_advt_no?: string;
  noc_date?: string;
  qualification_details?: string;
  payable_amount?: number;
  // surety_flag/surety_details are auto-fill only (fetched from the real
  // sap_loan_sureties data server-side) — not part of the HR-editable set.
  items_in_custody?: "Y" | "N";
  items_in_custody_text?: string;
  temp_charge_officer?: string;
  training_bond?: "Y" | "N";
  training_bond_period?: string;
  training_bond_amount?: number;
  training_bond_details?: string;
  foreign_training_bond?: "Y" | "N";
  foreign_training_period?: string;
  foreign_training_bond_amount?: number;
  foreign_training_details?: string;
  other_bond_submitted?: "Y" | "N";
  other_bond_details?: string;
  ccl_lwp?: "Y" | "N";
  ccl_lwp_period?: string;
  ccl_availed?: "Y" | "N";
  ccl_period?: string;
  lwp_availed?: "Y" | "N";
  lwp_period?: string;
  eol_availed?: "Y" | "N";
  eol_details?: string;
  withdraw_last_yr?: "Y" | "N";
  withdraw_last_yr_details?: string;
  deputation_availed?: "Y" | "N";
  deputation_remittance_received?: "Y" | "N";
  pending_cases_details?: string;
  bond_transfer?: "Y" | "N";
  gratuity_transfer?: "Y" | "N";
  separation_type?: string;
  loans?: { loan_type: string; amount?: number; reference_number?: string; description?: string }[];
  loan_sureties?: { loan_type: string; surety_emp_id: string }[];
  bond_selections?: BondSelection[];
  deputation_history?: { organization_name: string; duration?: string }[];
}

export const hrUpdateResignationRequest = (
  data: HrUpdateRequestFields
): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.hrUpdateRequest, data));

export const saveDraftResignation = (
  _data: Partial<ResignationRequest>
): Promise<ApiResult<any>> =>
  Promise.resolve({ success: true, data: { message: "Draft saved locally" } });

/** Register an already-uploaded S3 document against a resignation request. */
export const uploadResignationDoc = (data: {
  request_id: string;
  doc_type: string;
  s3_key: string;
  file_name: string;
}): Promise<ApiResult<any>> =>
  withFallback(
    () =>
      Api.post(ESEP.upload, {
        request_id: data.request_id,
        doc_type: normalizeDocType(data.doc_type),
        s3_key: data.s3_key,
        file_name: data.file_name,
      }),
    {}
  );

export const getResignationDetails = async (
  id: string
): Promise<ApiResult<any>> => {
  try {
    const res = await Api.post(ESEP.findOne, { request_id: id });
    return { success: true, data: flattenDetail(res) };
  } catch (e: any) {
    return { success: true, data: null, message: Api.getErrorMessage(e) };
  }
};

/** Map backend separation_type → UI friendly type. */
export const mapSeparationType = (t?: string): string => {
  const s = (t || "").toUpperCase();
  if (s.includes("SHORT")) return "Short Notice";
  if (s.includes("WITHDRAW")) return "Withdrawal";
  if (s.includes("BOND") || s.includes("WAIVER")) return "Bond Waiver";
  return "Intimation";
};

/** Map a backend request_type → UI SeparationType (mirrors the mobile app). */
export const requestTypeToLabel = (requestType?: string): SeparationType => {
  switch (String(requestType ?? "").toUpperCase()) {
    case "SHORT_NOTICE":
      return "ShortNotice";
    case "WITHDRAWAL":
      return "Withdrawal";
    case "NO_DUES":
      return "NoDues";
    case "TERMINATION":
      return "Termination";
    case "DEATH":
      return "Death";
    case "RETIREMENT":
      return "Retirement";
    case "DEPUTATION":
      return "Deputation";
    case "TRANSFER_OUT":
      return "TransferOut";
    default:
      return "Intimation";
  }
};


export const SEPARATION_MODE_LABELS: Record<string, string> = {
  RESIGNATION: "Resignation",
  RETIREMENT: "Retirement",
  DEPUTATION: "Deputation",
  TERMINATION: "Termination",
  DEATH: "Death",
  TRANSFER_OUT: "Transfer-Out",
  SHORT_NOTICE: "Short Notice",
  WITHDRAWAL: "Withdrawal",
  NO_DUES: "No-Dues",
};

export const formatSeparationMode = (value?: string | null): string => {
  const v = String(value ?? "").trim();
  if (!v) return "—";
  return SEPARATION_MODE_LABELS[v.toUpperCase()] || v;
};

/** Map raw backend status code → readable label (also drives StatusBadge colour). */
export const prettyStatus = (s?: string): string => {
  const u = (s || "").toUpperCase();
  const map: Record<string, string> = {
    SUBMITTED: "Submitted",
    DRAFT: "Draft",
    NO_DUES_PENDING: "No-Dues Form Pending",
    NO_DUES_IN_PROGRESS: "No-Dues In Progress",
    NO_DUES_ESTABLISHMENT: "With Establishment",
    PENDING_APPROVAL: "Pending Approval",
    SENT_BACK: "Sent Back",
    CLARIFICATION_PENDING: "Clarification",
    REACHED_HR: "Reached HR",
    DEPUTY_APPR: "Deputy Approved",
    CO_APPR: "CO Approved",
    HOD_APPR: "HOD Approved",
    HR_APPR: "HR Approved",
    CA_APPR: "CA Approved",
    MD_APPR: "MD Approved",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    CLARIFICATION: "Clarification",
    WITHDRAWN: "Withdrawn",
    SUPERSEDED: "Superseded",
    CLOSED: "Closed",
    COMPLETED: "Completed",
    IN_REVIEW: "In Review",
  };
  if (map[u]) return map[u];
  if (!u) return "—";
  return u
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Map one raw resignation record → SeparationListItem. */
export const mapResignationToListItem = (r: any): SeparationListItem => ({
  id: r?.request_id || r?.id || "—",
  type: r?.request_type || r?.separation_type || "Resignation",
  status: r?.status || "—",
  display_status: r?.display_status || undefined,
  date: formatDate(r?.intimation_date),
  nextAction:
    r?.next_approver_role ||
    (r?.display_status ? prettyStatus(r.display_status) : r?.status) ||
    "Pending",
  emp_no: r?.emp_no,
  relieve_date: formatDate(
    r?.requested_relieve_date || r?.approved_wef || r?.notice_end_date
  ),
  raw: r,
});

export const getMyResignations = (
  empNo?: string
): Promise<ApiResult<SeparationListItem[]>> =>
  withFallback(
    () =>
      Api.post(ESEP.findAll, {
        scope: "mine",
        emp_no: empNo || getCurrentEmpNo(),
      }),
    [],
    (p) => extractList({ data: p }).map(mapResignationToListItem)
  );

/* ─────────────── Outstanding loans (auto-captured from SAP) ─────────────── */
export interface OutstandingLoan {
  reference_number: string | null;
  loan_code: string | null;
  loan_name: string | null;
  granted_amount: number;
  balance: number;
  status: string | null;
}

// The 7 fixed categories on "Indicate Loan or Advances" (Application for
// Intimation of Resignation, field 25). A "SAP_OVERRIDE" row (with
// reference_number set) instead records the employee's corrected amount for
// a specific auto-fetched SAP loan — never written back to sap_loan_requests.
export const DECLARED_LOAN_TYPES: { code: string; label: string }[] = [
  { code: "HBA", label: "HBA" },
  { code: "MPA", label: "MPA" },
  { code: "LAPTOP", label: "Laptop Adv." },
  { code: "VEHICLE", label: "Veh. Adv." },
  { code: "DECCS", label: "DECCS" },
  { code: "MED_LTC", label: "Med./LTC Adv." },
  { code: "OTHERS", label: "Others" },
];

// A saved esep_loan row, as returned by find-one — either a field-25
// declaration (reference_number null) or a SAP-loan amount correction
// (reference_number set to that loan's own reference number).
export interface DeclaredLoanRecord {
  id?: number;
  loan_type: string;
  amount: number;
  reference_number?: string | null;
  description?: string | null;
}

// One row the employee (or HR) is editing before submit — same shape sent in
// the submit/resubmit/HR-update payload.
export interface LoanSuretyItem {
  loan_type: string;
  surety_emp_id: string;
}

// A saved esep_loan_surety row, as returned by find-one.
export interface LoanSuretyRecord extends LoanSuretyItem {
  id?: number;
  surety_emp_name?: string | null;
}

// Auto-captures an employee's outstanding loans (positive balance) from the SAP
// loan table. Pass an emp_no to fetch another employee's loans (HR/approver);
// omit it to fetch the caller's own.
export const getOutstandingLoans = async (
  empNo?: string
): Promise<OutstandingLoan[]> => {
  try {
    const res = await Api.post(
      ESEP.outstandingLoans,
      empNo ? { emp_no: empNo } : {}
    );
    const list = extractList({ data: unwrap(res) });
    return (list || []).map((r: any) => ({
      reference_number: r.reference_number ?? null,
      loan_code: r.loan_code ?? null,
      loan_name: r.loan_name ?? null,
      granted_amount: Number(r.granted_amount) || 0,
      balance: Number(r.balance) || 0,
      status: r.status ?? null,
    }));
  } catch (err) {
    console.log("getOutstandingLoans error:", err);
    return [];
  }
};

/* ─── Resignation form prefill: one call for every auto-fill field ─── */
export interface HierarchyPersonDetail {
  name: string | null;
  designation: string | null;
  emp_no: string | null;
  department: string | null;
}

export interface SuretyPerson {
  empId: string;
  name: string;
}

export interface LoanSurety {
  empId: string;
  loanType: string;
  loanText: string;
  firstSurety: SuretyPerson;
  secondSurety: SuretyPerson;
}

// One CCL or LWP period, as synced from SAP into sap_lwp_records.
export interface LwpLikeRecord {
  start_date: string | null;
  end_date: string | null;
  days: number;
}

export interface ResignationPrefill {
  emp_no: string;
  emp_group: string | null;
  wing: string | null;
  emp_name: string | null;
  designation: string | null;
  emp_type: string | null;
  posting: string | null;
  pay_scale: string | null;
  doj: string | null;
  email: string | null;
  mobile: string | null;
  // No synced SAP source yet — always null.
  customer_specific_status: string | null;
  payroll_area: string | null;
  co_name: string | null;
  co_designation: string | null;
  co_emp_no: string | null;
  co_department: string | null;
  co_mobile: string | null;
  dy_hod_name: string | null;
  dy_hod_designation: string | null;
  dy_hod_emp_no: string | null;
  dy_hod_department: string | null;
  hod_name: string | null;
  hod_designation: string | null;
  hod_emp_no: string | null;
  hod_department: string | null;
  director_name: string | null;
  director_designation: string | null;
  director_emp_no: string | null;
  director_department: string | null;
  // Auto-fill only — fetched from the real sap_loan_sureties data.
  surety_flag: string;
  surety_details: string | null;
  // Raw basic pay (SAP master) — for previewing the Short Notice Period
  // Amount formula; the backend always recomputes the authoritative value.
  basic_pay: number;
  // Raw separation fields (SAP sync) — drive isRetirement/isSuperannuation/isDeputation.
  separation_date: string | null;
  action_type: string | null;
  action_type_text: string | null;
  intimation_date: string;
  notice_end_date: string;
  outstanding_loans: OutstandingLoan[];
  loan_sureties: LoanSurety[];
  // "Child Care Leave (CCL) / Leave Without Pay (LWP) taken in last 2
  // years?" — auto-fetched from the SAP-synced sap_lwp_records table, never
  // employee-editable. ccl_records is only ever non-empty for female
  // employees (SAP itself never records CHLC against a male employee), but
  // the form still gates the section on emp.gender defensively.
  ccl_records: LwpLikeRecord[];
  lwp_records: LwpLikeRecord[];
  bond_types: BondType[];
  // Temporary Residence -> prefills Correspondence/Present Address (stays
  // editable). Home Town -> prefills the read-only Home Address.
  temporary_residence_address: EmployeeAddressDetail | null;
  home_town_address: EmployeeAddressDetail | null;
  isRetirement: boolean;
  isSuperannuation: boolean;
  isDeputation: boolean;
}

export interface EmployeeAddressDetail {
  care_of: string | null;
  house_number: string | null;
  street: string | null;
  address_line_2: string | null;
  city: string | null;
  district: string | null;
  postal_code: string | null;
  country: string | null;
  telephone_no: string | null;
  formatted: string;
}

// Single consolidated prefill for the "Application for Intimation of
// Resignation" form — every auto-fill field, outstanding loans, loan
// sureties, and active bond types in one call.
export const getResignationPrefill = (): Promise<ApiResult<ResignationPrefill | null>> =>
  withFallback(() => Api.post(ESEP.prefill, {}), null);

/* ─────────────── HR-initiated separation (Termination / Death / ...) ─────────────── */
export interface EmployeeSearchResult {
  emp_no: string;
  emp_name: string;
  designation: string;
  department: string;
}

export const searchEmployees = async (
  query: string
): Promise<EmployeeSearchResult[]> => {
  const q = String(query ?? "").trim();
  if (!q) return [];
  try {
    const isNum = /^\d+$/.test(q);
    const res = await Api.post(EMPLOYEES_FIND, {
      page: 1,
      limit: 10,
      filters: {
        firstname: isNum ? "" : q,
        lsastname: "",
        empId: isNum ? q : "",
        email: "",
        phone: "",
        wing: "",
        department: "",
        designation: "",
      },
    });
    const list = extractList({ data: unwrap(res) });
    const results = (list || [])
      .map((item: any) => {
        const d = item?.details ?? item;
        const name = [d?.firstname, d?.secondname, d?.lsastname]
          .map((p: any) => String(p ?? "").trim())
          .filter(Boolean)
          .join(" ");
        return {
          emp_no: String(d?.empId ?? item?.empId ?? item?.emp_no ?? "").trim(),
          emp_name: name || d?.fullName || d?.emp_name || "",
          designation:
            d?.designation_name || d?.designation || d?.designationID || "",
          department: d?.department_name || d?.department || "",
        };
      })
      .filter((e: EmployeeSearchResult) => e.emp_no);

    // If query is numeric (employee ID) and not in list, check EMPLOYEE_PROFILE directly
    if (results.length === 0 && isNum) {
      const padded = q.padStart(8, "0");
      try {
        const profRes = await Api.post(EMPLOYEE_PROFILE, { emp_no: padded });
        const p = unwrap(profRes);
        const pd = p?.personal_details || p;
        if (pd && (pd.firstname || pd.emp_name || pd.fullName || pd.empId || p?.emp_no)) {
          const name =
            [pd.firstname, pd.secondname, pd.lsastname]
              .filter(Boolean)
              .join(" ")
              .trim() || pd.fullName || pd.emp_name || "";
          if (name) {
            results.push({
              emp_no: String(p?.emp_no || pd.empId || pd.emp_no || padded).trim(),
              emp_name: name,
              designation: pd.designation_name || pd.designation || "",
              department: pd.department_name || pd.department || "",
            });
          }
        }
      } catch {
        // not found
      }
    }
    return results;
  } catch (err) {
    console.log("searchEmployees error:", err);
    return [];
  }
};

export const validateEmployeeNo = async (
  empNo: string
): Promise<{
  valid: boolean;
  data?: EmployeeSearchResult;
  message?: string;
}> => {
  const raw = String(empNo ?? "").trim();
  if (!raw) return { valid: false, message: "Employee number cannot be blank." };
  const padded = raw.padStart(8, "0");
  const list = await searchEmployees(padded);
  const match = list.find(
    (e) =>
      e.emp_no === padded ||
      e.emp_no === raw ||
      e.emp_no.replace(/^0+/, "") === raw.replace(/^0+/, "")
  );
  if (match) {
    return { valid: true, data: match };
  }
  if (list.length > 0) {
    return { valid: true, data: list[0] };
  }
  return {
    valid: false,
    message: "Invalid employee no",
  };
};

export interface HrInitiatedCaseItem {
  id: string;
  type: SeparationType;
  request_type: string;
  emp_name: string;
  emp_no: string;
  designation: string;
  posting: string;
  date: string;
  status: string;
  // Derived No-Dues status for the new (no-approval) cases; undefined for an
  // older case that still carries a real approval chain.
  display_status?: string;
  initiated_by?: "HR" | "EMPLOYEE";
  no_dues_started: boolean;
}


export const submitHrInitiatedCase = (data: {
  emp_no: string;
  case_type:
    | "TERMINATION"
    | "DEATH"
    | "DEPUTATION"
    | "RETIREMENT"
    | "TRANSFER_OUT";
  date_of_leaving: string;
  reason: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.hrInitiate, {
      emp_no: String(data.emp_no ?? "").trim(),
      case_type: data.case_type,
      date_of_leaving: data.date_of_leaving,
      reason: data.reason,
    })
  );

// All No-Dues-only separation cases (any type, HR- or employee-initiated) for the
// HR dashboard.
export const getHrInitiatedCases = async (): Promise<HrInitiatedCaseItem[]> => {
  try {
    const res = await Api.post(ESEP.hrInitiateList, {});
    const body = extractList({ data: unwrap(res) });
    const items: HrInitiatedCaseItem[] = (body || []).map((r: any) => ({
      id: r.request_id,
      type: requestTypeToLabel(r.request_type),
      request_type: r.request_type || "",
      emp_name: r.emp_name || "Employee",
      emp_no: r.emp_no || "",
      designation: r.designation || "",
      posting: r.posting || "",
      date: r.intimation_date || "",
      status: r.status || "",
      display_status: r.display_status || undefined,
      initiated_by: r.initiated_by || undefined,
      no_dues_started: !!r.no_dues_started,
    }));

    const backendHasFlag = (body || []).some((r: any) =>
      Object.prototype.hasOwnProperty.call(r, "no_dues_started")
    );
    if (!backendHasFlag) {
      await Promise.all(
        items.map(async (it) => {
          if (!ESEP_NO_DUES_ONLY_TYPES.includes(it.request_type)) return;
          try {
            const ndRes = await Api.post(ESEP.noDuesFindOne, {
              request_id: it.id,
            });
            const nd = unwrapDeep(ndRes);
            it.no_dues_started =
              String(nd?.status ?? "").toUpperCase() === "SUBMITTED" ||
              !!nd?.nodues_request_id;
          } catch {
            // leave as-is on error
          }
        })
      );
    }
    return items;
  } catch (err) {
    console.log("getHrInitiatedCases error:", err);
    return [];
  }
};

// Short notice — inherits every detail from the original resignation; the only
// new input is the desired relieving date. Submitting auto-jumps the original
// resignation to HR regardless of the stage it had reached.
export interface ShortNoticeSubmitPayload {
  request_id: string;
  desired_relieve_date?: string;
  reason?: string;
}

export const submitShortNotice = (
  data: ShortNoticeSubmitPayload
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.shortNotice, {
      orig_request_id: data.request_id,
      desired_relieve_date: data.desired_relieve_date,
      reason: data.reason || "",
    })
  );

export interface ShortNoticeUploadPayload {
  short_notice_id: string;
  doc_type: string;
  file_name: string;
  s3_key: string;
}

export const uploadShortNoticeDoc = (
  data: ShortNoticeUploadPayload
): Promise<ApiResult<any>> =>
  withFallback(
    () =>
      Api.post(ESEP.upload, {
        request_id: data.short_notice_id,
        doc_type: normalizeDocType(data.doc_type || "SN_ATTACHMENT"),
        s3_key: data.s3_key,
        file_name: data.file_name,
      }),
    {}
  );

/** Upload a raw file to S3 (aws-bucket/upload-file) and return its key + url. */
export const uploadFileToS3 = async (
  file: File
): Promise<{ key: string; url: string } | null> => {
  try {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (e) => reject(e);
    });
    const resp: any = await Api.post(AWS_UPLOAD, {
      filestring: base64,
      module: "e-separation",
      mime_type: file.type,
    });
    const ud = resp?.data || resp || {};
    const data = ud?.data || ud;
    if (!data?.key) return null;
    return { key: data.key, url: data.url };
  } catch {
    return null;
  }
};

// Resolves a document's S3 key to a fresh, time-limited signed URL for
// viewing/downloading. Called on-demand (not cached) since presigned URLs
// expire — matches the `getPresignedUrl` thunk used elsewhere in the app.
export const getDocumentUrl = async (s3Key: string): Promise<string | null> => {
  try {
    const resp: any = await Api.post(AWS_GET_PRESIGNED_URL, { key: s3Key });
    return typeof resp?.data === "string" ? resp.data : null;
  } catch {
    return null;
  }
};

export const getShortNoticeDetails = (id: string): Promise<ApiResult<any>> =>
  getResignationDetails(id);

// Withdrawal — allowed only while the resignation is still before HR.
export const submitWithdrawal = (
  data: WithdrawalRequest & { orig_request_id?: string; withdrawal_reason?: string }
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.withdrawal, {
      orig_request_id: data.orig_request_id || data.resignation_request_id,
      withdrawal_reason: data.withdrawal_reason || data.reason || "",
    })
  );

export const getWithdrawalDetails = (id: string): Promise<ApiResult<any>> =>
  getResignationDetails(id);

// Eligibility = original resignation has not yet reached HR / been decided.
export const checkWithdrawalEligibility = async (
  origRequestId: string
): Promise<ApiResult<{ eligible: boolean; approving_authority?: string }>> => {
  try {
    const detail = await getResignationDetails(origRequestId);
    const status = String(detail?.data?.status ?? "").toUpperCase();
    const blocked = ["REACHED_HR", "APPROVED", "REJECTED", "WITHDRAWN"].includes(
      status
    );
    return { success: true, data: { eligible: !blocked } };
  } catch (e: any) {
    return { success: true, data: { eligible: false }, message: Api.getErrorMessage(e) };
  }
};

// Bond Waiver — removed from esep/ v2 (handled outside this module). Stubbed.
export const submitBondWaiver = (
  _data: BondWaiverRequest
): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: false,
    message: "Bond waiver is not part of the e-separation v2 flow.",
  });

export const getBondWaiverDetails = (id: string): Promise<ApiResult<any>> =>
  getResignationDetails(id);

export const checkBondWaiverEligibility = (
  _empNo?: string
): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: true,
    data: { has_active_resignation: false, has_active_bond: false, eligible: false },
  });

/* ─────────────────── No-Dues (employee + tracker) ─────────────────── */
// Employee No-Dues submission (enabled once the resignation reaches HR).
// request_id is the originating resignation id; the backend creates the
// dedicated NO_DUES request, builds its chain, and stores the form details.
export const submitNoDues = (data: {
  request_id: string;
  details?: Record<string, any>;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesSubmit, {
      request_id: data.request_id,
      details: data.details ?? {},
    })
  );

// Employee initiates their own No-Dues-only case (deputation /
// deputation / retirement / transfer-out). There is no resignation approval flow:
// the case is created with its No-Dues form enabled — the returned request_id is
// then passed to submitNoDues together with the filled form.
export const initiateNoDuesCase = (data: {
  case_type: "DEPUTATION" | "RETIREMENT" | "TRANSFER_OUT";
  date_of_leaving: string;
  reason: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesInitiateCase, {
      case_type: data.case_type,
      date_of_leaving: data.date_of_leaving,
      reason: data.reason,
    })
  );

// Tracker — resolve the active resignation, then read its No-Dues row.
export const getNoDuesStatus = async (
  empNo?: string
): Promise<ApiResult<NoDuesCase | null>> => {
  const emp_no = empNo || getCurrentEmpNo();
  try {
    const listRes = await Api.post(ESEP.findAll, { scope: "mine", emp_no });
    const resignations = extractList({ data: unwrap(listRes) });
    const activeRes = resignations.find((r: any) =>
      ["REACHED_HR", "CA_APPROVED", "ON_HOLD", "DEPT_CLEARANCE", "PENDING_APPROVAL", "APPROVED"].includes(
        String(r.status ?? "").toUpperCase()
      )
    );
    if (activeRes) {
      // `mine` also lists the NO_DUES child (ND-…) of the resignation. Documents
      // such as the No-Dues Certificate are generated from the PARENT resignation /
      // case, so when the matched row is the child, step up to its parent.
      const parentRequestId: string =
        String(activeRes.request_type ?? "").toUpperCase() === "NO_DUES" && activeRes.orig_request_id
          ? activeRes.orig_request_id
          : activeRes.request_id;
      const res = await Api.post(ESEP.noDuesFindOne, {
        request_id: activeRes.request_id,
      });
      const nd = unwrapDeep(res);
      if (nd) {
        const baseCase = mapV2NoDuesToUI(nd, emp_no);
        if (nd.nodues_request_id) {
          try {
            const dispatchRes = await getNoDuesDispatchList(
              nd.nodues_request_id
            );
            const dispatches = dispatchRes.data || [];
            if (dispatches.length > 0) {
              const map: Record<string, NoDuesDepartment["status"]> = {
                CLEARED: "Cleared",
                PENDING: "Pending",
                REJECTED: "Dues Found",
              };
              const departments: NoDuesDepartment[] = dispatches.map((d) => ({
                dept_name: d.department_name,
                nodal_officer: d.approver_emp_id || "—",
                status: map[String(d.status).toUpperCase()] || "Pending",
                remarks: d.remarks || "",
                due_items: [],
                recovery_amount: d.recovery_amount
                  ? Number(d.recovery_amount)
                  : 0,
                dispatch_id: d.id,
              }));
              const clearedCount = dispatches.filter(
                (d) => String(d.status).toUpperCase() === "CLEARED"
              ).length;
              const totalRecovery = dispatches.reduce(
                (sum, d) =>
                  sum + (d.recovery_amount ? Number(d.recovery_amount) : 0),
                0
              );
              return {
                success: true,
                data: {
                  ...baseCase,
                  total_departments: dispatches.length,
                  cleared_departments: clearedCount,
                  departments,
                  finance_summary: {
                    ...baseCase.finance_summary,
                    total_recoverable: totalRecovery,
                  },
                  nodues_request_id: nd.nodues_request_id,
                  request_id: parentRequestId,
                },
              };
            }
          } catch {
          }
        }
        return {
          success: true,
          data: { ...baseCase, nodues_request_id: nd.nodues_request_id, request_id: parentRequestId },
        };
      }
    }
  } catch (e: any) {
    return {
      success: true,
      data: mapV2NoDuesToUI(null, emp_no),
      message: Api.getErrorMessage(e),
    };
  }
  return { success: true, data: mapV2NoDuesToUI(null, emp_no) };
};

export const getNoDuesProgress = async (
  resignationRequestId: string
): Promise<{ submitted: boolean; dispatched: boolean; ndRequestId: string }> => {
  try {
    const res = await Api.post(ESEP.noDuesFindOne, {
      request_id: resignationRequestId,
    });
    const nd = unwrapDeep(res);
    const ndRequestId = nd?.nodues_request_id || "";
    const submitted =
      String(nd?.status ?? "").toUpperCase() === "SUBMITTED" || !!ndRequestId;
    let dispatched = false;
    if (ndRequestId) {
      const rows = await getNoDuesDispatchList(ndRequestId);
      dispatched = (rows.data || []).length > 0;
    }
    return { submitted, dispatched, ndRequestId };
  } catch {
    return { submitted: false, dispatched: false, ndRequestId: "" };
  }
};

// Fetch a No-Dues case directly by resignation request_id (HR final review).
export const getNoDuesByRequest = async (
  requestId: string
): Promise<ApiResult<NoDuesCase | null>> => {
  try {
    const res = await Api.post(ESEP.noDuesFindOne, { request_id: requestId });
    const nd = unwrapDeep(res);
    if (nd) return { success: true, data: mapV2NoDuesToUI(nd) };
    return { success: true, data: null };
  } catch (e: any) {
    return { success: true, data: null, message: Api.getErrorMessage(e) };
  }
};

export const getNoDuesDepartments = (
  _caseId: string
): Promise<ApiResult<NoDuesDepartment[]>> =>
  Promise.resolve({
    success: true,
    data: ND_DEPARTMENTS.map((d) => ({
      dept_name: d,
      nodal_officer: "—",
      status: "Pending" as const,
    })),
  });

/* ─── No-Dues: HR dispatch + dept clearance + admin config ─── */
// HR: dispatch a No-Dues request to selected departments.
export const dispatchNoDues = (data: {
  request_id: string;
  department_ids: number[];
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesDispatch, {
      request_id: data.request_id,
      department_ids: data.department_ids,
      comments: data.comments ?? "",
    })
  );

// HR: dispatch a No-Dues request to every active clearing department in one
// action — the single-button replacement for manually picking departments.
export const dispatchAllNoDues = (data: {
  request_id: string;
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesDispatchAll, {
      request_id: data.request_id,
      comments: data.comments ?? "",
    })
  );

// The per-department clearance rows for a No-Dues request.
export const getNoDuesDispatchList = (
  requestId: string
): Promise<ApiResult<NoDuesDispatchRow[]>> =>
  withFallback(
    () => Api.post(ESEP.noDuesDispatchList, { request_id: requestId }),
    [],
    (p) => extractList({ data: p }) as NoDuesDispatchRow[]
  );

export const submitNoDuesDeptClearance = (data: {
  dispatch_id: number;
  status: "CLEARED" | "REJECTED";
  summary?: string;
  remarks?: string;
  recovery_amount?: number;
  items?: NoDuesDeptChecklistItem[];
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesDeptAction, {
      dispatch_id: data.dispatch_id,
      status: data.status,
      summary: data.summary ?? data.remarks ?? "",
      remarks: data.remarks ?? data.summary ?? "",
      recovery_amount: data.recovery_amount ?? 0,
      items: (data.items ?? []).map((i) => ({
        item_name: i.item_name,
        amount_due: Number(i.amount_due) || 0,
        comments: i.comments ?? "",
      })),
    })
  );

// Department approver: pending clearances on their desk.
export const getNoDuesDeptInbox = (): Promise<ApiResult<NoDuesDispatchRow[]>> =>
  withFallback(
    () => Api.post(ESEP.noDuesDeptInbox, {}),
    [],
    (p) => extractList({ data: p }) as NoDuesDispatchRow[]
  );

export const getNoDuesDeptProcessed = (): Promise<
  ApiResult<NoDuesDispatchRow[]>
> =>
  withFallback(
    () => Api.post(ESEP.noDuesDeptProcessed, {}),
    [],
    (p) => extractList({ data: p }) as NoDuesDispatchRow[]
  );

export const getNoDuesDeptDetail = (
  dispatchId: number
): Promise<ApiResult<NoDuesDeptDetail | null>> =>
  withFallback(
    () => Api.post(ESEP.noDuesDeptDetail, { dispatch_id: dispatchId }),
    null,
    (p) => unwrapDeep({ data: p }) as NoDuesDeptDetail
  );

export const getNoDuesSavedItems = (
  departmentId: number
): Promise<ApiResult<NoDuesSavedItem[]>> =>
  withFallback(
    () => Api.post(ESEP.noDuesSavedItemsList, { department_id: departmentId }),
    [],
    (p) => extractList({ data: p }) as NoDuesSavedItem[]
  );

export const saveNoDuesSavedItem = (data: {
  department_id: number;
  item_name: string;
  amount_due?: number;
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesSavedItemsSave, {
      department_id: data.department_id,
      item_name: data.item_name,
      amount_due: Number(data.amount_due) || 0,
      comments: data.comments ?? "",
    })
  );

export const deleteNoDuesSavedItem = (id: number): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.noDuesSavedItemsDelete, { id }));

export const getNoDuesFormDetails = (
  requestId: string
): Promise<ApiResult<any>> =>
  withFallback(
    () => Api.post(ESEP.noDuesFindOne, { request_id: requestId }),
    null,
    (p) => unwrapDeep({ data: p })
  );

// Admin: list configured clearing departments.
export const getNoDuesDepartmentsConfig = (): Promise<
  ApiResult<NoDuesDepartmentConfig[]>
> =>
  withFallback(
    () => Api.post(ESEP.adminNoDuesDeptList, {}),
    [],
    (p) => extractList({ data: p }) as NoDuesDepartmentConfig[]
  );

// Admin: create / update a department + its approver.
export const saveNoDuesDepartment = (
  data: Partial<NoDuesDepartmentConfig>
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.adminNoDuesDept, {
      id: data.id,
      code: data.code,
      name: data.name,
      approver_emp_id: data.approver_emp_id ?? null,
      is_active: data.is_active ?? true,
      is_settlement: data.is_settlement ?? false,
      sort_order: data.sort_order ?? 0,
    })
  );

// Admin: remove a department.
export const deleteNoDuesDepartment = (id: number): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.adminNoDuesDeptDelete, { id }));

/* ─── No-Dues: Sub-Nodal delegation ───────────────────────────────────── */
// Nodal Officer: register a sub-nodal for one of their own departments.
export const createSubNodal = (data: {
  department_id: number;
  sub_nodal_emp_id: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.subNodalCreate, {
      department_id: data.department_id,
      sub_nodal_emp_id: data.sub_nodal_emp_id,
    })
  );

// Nodal Officer: every sub-nodal they've registered, across all their departments.
export const listMySubNodals = (): Promise<ApiResult<NoDuesSubNodal[]>> =>
  withFallback(
    () => Api.post(ESEP.subNodalList, {}),
    [],
    (p) => extractList({ data: p }) as NoDuesSubNodal[]
  );

export const deleteSubNodal = (id: number): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.subNodalDelete, { id }));

// Nodal Officer: hand one pending clearance row off to a registered sub-nodal.
export const delegateDispatch = (data: {
  dispatch_id: number;
  sub_nodal_emp_id: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.subNodalDelegate, {
      dispatch_id: data.dispatch_id,
      sub_nodal_emp_id: data.sub_nodal_emp_id,
    })
  );

// Nodal Officer: take a delegated row back onto their own desk.
export const revokeDelegation = (dispatchId: number): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.subNodalRevoke, { dispatch_id: dispatchId }));

// Nodal Officer: pending rows they've delegated away (doesn't show in their own
// dept-inbox once delegated — this is the only place to see/revoke them).
export const getDelegatedByMe = (): Promise<ApiResult<NoDuesDispatchRow[]>> =>
  withFallback(
    () => Api.post(ESEP.subNodalDelegatedByMe, {}),
    [],
    (p) => extractList({ data: p }) as NoDuesDispatchRow[]
  );

// Bond types — HR admin list (all, incl. inactive) or employee-facing
// (active-only). Both the resignation form and the HR admin editor call this.
export const getBondTypes = (activeOnly = false): Promise<ApiResult<BondType[]>> =>
  withFallback(
    () => Api.post(ESEP.adminBondTypeList, { active_only: activeOnly }),
    [],
    (p) => extractList({ data: p }) as BondType[]
  );

// Admin: create / update a bond type + replace its dynamic fields[] in one call.
export const saveBondType = (
  data: Partial<BondType>
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.adminBondType, {
      id: data.id,
      code: data.code,
      name: data.name,
      description: data.description ?? null,
      is_active: data.is_active ?? true,
      sort_order: data.sort_order ?? 0,
      fields: (data.fields ?? []).map((f) => ({
        id: f.id,
        field_key: f.field_key,
        field_label: f.field_label,
        field_type: f.field_type,
        is_required: f.is_required ?? false,
        sort_order: f.sort_order ?? 0,
      })),
    })
  );

// Admin: remove a bond type.
export const deleteBondType = (id: number): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.adminBondTypeDelete, { id }));

/* ─── Vigilance: single global officer assignment + Send-to-Vigilance cases ─── */
// Admin: who is currently the vigilance officer (null if never assigned).
export const getVigilanceOfficerConfig = (): Promise<
  ApiResult<VigilanceOfficerConfig>
> =>
  withFallback(
    () => Api.post(ESEP.adminVigilanceConfig, {}),
    { officer_emp_id: null }
  );

// Admin: assign (or reassign) the single vigilance officer by emp id.
export const saveVigilanceOfficerConfig = (
  officerEmpId: string
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.adminVigilanceConfigSave, { officer_emp_id: officerEmpId })
  );

// HR: send (or re-send) a resignation request to the configured vigilance officer.
export const sendToVigilance = (
  requestId: string
): Promise<ApiResult<VigilanceCaseRecord>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.vigilanceSend, { request_id: requestId })
  );

// The current vigilance case for a request (null if never sent).
export const getVigilanceCase = (
  requestId: string
): Promise<ApiResult<VigilanceCaseRecord | null>> =>
  withFallback(
    () => Api.post(ESEP.vigilanceCase, { request_id: requestId }),
    null
  );

// Vigilance nodal / sub-nodal: the cases on their desk (the nodal's pending and
// returned cases, or a sub-nodal's delegated ones).
export const getVigilanceInbox = (): Promise<
  ApiResult<VigilanceCaseRecord[]>
> =>
  withFallback(
    () => Api.post(ESEP.vigilanceInbox, {}),
    [],
    (p) => extractList({ data: p }) as VigilanceCaseRecord[]
  );

// Cases the caller has already dealt with (Cleared / Hold / Objection, or submitted back by a sub-nodal).
export const getVigilanceProcessed = (): Promise<
  ApiResult<VigilanceCaseRecord[]>
> =>
  withFallback(
    () => Api.post(ESEP.vigilanceProcessed, {}),
    [],
    (p) => extractList({ data: p }) as VigilanceCaseRecord[]
  );

// Vigilance nodal: record the final outcome for a case (this is what goes to HR).
export const submitVigilanceAction = (data: {
  case_id: number;
  status: VigilanceDecision;
  remarks?: string;
  pending_case_ref?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.vigilanceAction, {
      case_id: data.case_id,
      status: data.status,
      remarks: data.remarks ?? "",
      pending_case_ref: data.pending_case_ref ?? "",
    })
  );


/* ─── Vigilance: sub-nodal delegation ─── */
// Vigilance nodal: register a sub-nodal.
export const createVigilanceSubNodal = (
  subNodalEmpId: string
): Promise<ApiResult<VigilanceSubNodal>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.vigilanceSubNodalCreate, { sub_nodal_emp_id: subNodalEmpId })
  );

export const listVigilanceSubNodals = (): Promise<
  ApiResult<VigilanceSubNodal[]>
> =>
  withFallback(
    () => Api.post(ESEP.vigilanceSubNodalList, {}),
    [],
    (p) => extractList({ data: p }) as VigilanceSubNodal[]
  );

export const deleteVigilanceSubNodal = (id: number): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.vigilanceSubNodalDelete, { id }));

// Vigilance nodal: hand one pending case to a registered sub-nodal.
export const delegateVigilanceCase = (data: {
  case_id: number;
  sub_nodal_emp_id: string;
}): Promise<ApiResult<VigilanceCaseRecord>> =>
  withFallbackStrict(() => Api.post(ESEP.vigilanceDelegate, data));

// Vigilance nodal: take a delegated case back onto their own desk.
export const revokeVigilanceDelegation = (
  caseId: number
): Promise<ApiResult<VigilanceCaseRecord>> =>
  withFallbackStrict(() => Api.post(ESEP.vigilanceRevoke, { case_id: caseId }));

// Vigilance nodal: cases they delegated that are still with the sub-nodal.
export const getVigilanceDelegatedByMe = (): Promise<
  ApiResult<VigilanceCaseRecord[]>
> =>
  withFallback(
    () => Api.post(ESEP.vigilanceDelegatedByMe, {}),
    [],
    (p) => extractList({ data: p }) as VigilanceCaseRecord[]
  );

// Vigilance sub-nodal: record findings on a delegated case. Not final — the case
// goes back to the nodal, who sends the outcome to HR.
export const submitVigilanceSubAction = (data: {
  case_id: number;
  status: VigilanceDecision;
  remarks?: string;
  pending_case_ref?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.vigilanceSubAction, {
      case_id: data.case_id,
      status: data.status,
      remarks: data.remarks ?? "",
      pending_case_ref: data.pending_case_ref ?? "",
    })
  );

export const getNoDuesSettlementStatus = (
  requestId: string
): Promise<ApiResult<NoDuesSettlementStatus | null>> =>
  withFallback(
    () => Api.post(ESEP.noDuesSettlementStatus, { request_id: requestId }),
    null,
    (p) => unwrapDeep({ data: p }) as NoDuesSettlementStatus
  );

// HR: send the consolidated recovery report to the settlement department.
export const sendNoDuesSettlement = (data: {
  request_id: string;
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesSettlementSend, {
      request_id: data.request_id,
      comments: data.comments ?? "",
    })
  );

// Settlement approver: in-progress settlements on their desk.
export const getNoDuesSettlementInbox = (): Promise<
  ApiResult<NoDuesSettlement[]>
> =>
  withFallback(
    () => Api.post(ESEP.noDuesSettlementInbox, {}),
    [],
    (p) => extractList({ data: p }) as NoDuesSettlement[]
  );

// Settlement approver: closed settlements on their desk.
export const getNoDuesSettlementProcessed = (): Promise<
  ApiResult<NoDuesSettlement[]>
> =>
  withFallback(
    () => Api.post(ESEP.noDuesSettlementProcessed, {}),
    [],
    (p) => extractList({ data: p }) as NoDuesSettlement[]
  );

// Full settlement detail: header, per-department items, employee identity.
export const getNoDuesSettlementDetail = (
  requestId: string
): Promise<ApiResult<NoDuesSettlementDetail | null>> =>
  withFallback(
    () => Api.post(ESEP.noDuesSettlementDetail, { request_id: requestId }),
    null,
    (p) => unwrapDeep({ data: p }) as NoDuesSettlementDetail
  );

// Settlement approver: settle one department-reported due (collected / not).
export const settleNoDuesItem = (data: {
  item_id: number;
  collected: boolean;
  collected_amount?: number;
  remarks?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesSettlementSettleItem, {
      item_id: data.item_id,
      collected: data.collected,
      collected_amount: Number(data.collected_amount) || 0,
      remarks: data.remarks ?? "",
    })
  );

// Settlement approver: close the settlement → finalizes the No-Dues request.
export const closeNoDuesSettlement = (data: {
  request_id: string;
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.noDuesSettlementClose, {
      request_id: data.request_id,
      comments: data.comments ?? "",
    })
  );

// Download the consolidated settlement report PDF (browser blob download).
export const downloadNoDuesSettlementReport = async (
  requestId: string
): Promise<ApiResult<any>> => {
  try {
    const token: string | null = tokenService.getToken();
    const guid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const endpointPath = "esep/no-dues/settlement/report/pdf";
    const body = {
      Header: {
        device_id: "WEB",
        device_token: "WEB",
        device_type: "WEB",
        guid,
        epoch: Date.now(),
        url: endpointPath,
        service_request_id: endpointPath,
      },
      Request: { data: { request_id: requestId } },
    };
    const resp = await axios({
      method: "POST",
      url: ESEP.noDuesSettlementReportPdf,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        "enc-req": 0,
        "enc-res": 0,
      },
      data: body,
      responseType: "arraybuffer",
    });
    const bytes = resp?.data;
    if (!bytes || (bytes.byteLength ?? 0) === 0) {
      return { success: false, message: "Empty PDF received from server." };
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `settlement_report_${requestId}_${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (e: any) {
    return { success: false, message: Api.getErrorMessage(e) };
  }
};

/* ─── HR wing config (per-wing HR approver) ─── */
export const getHrWingConfig = (): Promise<ApiResult<HrWingConfig[]>> =>
  withFallback(
    () => Api.post(ESEP.adminHrConfigList, {}),
    [],
    (p) => extractList({ data: p }) as HrWingConfig[]
  );

export const saveHrWingConfig = (data: {
  wing: string;
  hr_approver_emp_id: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.adminHrConfig, data));

/* ─── V2: HR level config (4-level roster, per wing + group roster) ─── */
export const getHrLevelConfig = (): Promise<ApiResult<EsepHrLevelConfig[]>> =>
  withFallback(
    () => Api.post(ESEP.adminHrLevelConfigList, {}),
    [],
    (p) => extractList({ data: p }) as EsepHrLevelConfig[]
  );

export const saveHrLevelConfig = (
  data: EsepHrLevelConfig
): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.adminHrLevelConfig, data));

/* ─── V2: Competent Authority (parallel track) ─── */
// `ca_emp_id` (optional) sends it to that employee instead of the configured CA.
export const submitSendToCA = (
  request_id: string,
  ca_emp_id?: string
): Promise<ApiResult<EsepCaApproval>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.caSend, { request_id, ...(ca_emp_id ? { ca_emp_id } : {}) })
  );

// Uses postKeepingAction (not plain Api.post) — the shared Api wrapper hoists
// any top-level `action` key out of the body into Header.action, which would
// make it vanish before reaching esep/ca/action's own `action` validation.
// Same reason submitApprovalAction (esep/approval/action) does this.
export const submitCaAction = (data: {
  request_id: string;
  action: "APPROVE" | "REJECT";
  comments?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => postKeepingAction(ESEP.caAction, data));

export const getCaInbox = (): Promise<ApiResult<any[]>> =>
  withFallback(() => Api.post(ESEP.caInbox, {}), [], (p) => extractList({ data: p }));

export const getCaDetail = (
  request_id: string
): Promise<ApiResult<EsepCaApproval | null>> =>
  withFallbackStrict(() => Api.post(ESEP.caDetail, { request_id }));

// True when the given empId is configured as an HR approver for any wing.
export const isCurrentUserHr = async (empId?: string): Promise<boolean> => {
  const norm = String(empId ?? getCurrentEmpNo() ?? "").trim().padStart(8, "0");
  if (!norm || norm === "00000000") return false;
  const res = await getHrWingConfig();
  return (res.data || []).some(
    (c: any) =>
      String(c?.hr_approver_emp_id ?? "").trim().padStart(8, "0") === norm
  );
};

export const getMyNoDuesApproverRoles = async (
  empId?: string
): Promise<{ isDeptApprover: boolean; isFinanceApprover: boolean }> => {
  const norm = String(empId ?? getCurrentEmpNo() ?? "").trim().padStart(8, "0");
  if (!norm || norm === "00000000")
    return { isDeptApprover: false, isFinanceApprover: false };
  const res = await getNoDuesDepartmentsConfig();
  const mine = (res.data || []).filter(
    (d) =>
      d.is_active &&
      String(d.approver_emp_id ?? "").trim().padStart(8, "0") === norm
  );
  return {
    isDeptApprover: mine.some((d) => !d.is_settlement),
    isFinanceApprover: mine.some((d) => d.is_settlement),
  };
};

/* ───────────────────── Capabilities API ───────────────────────────── */
// Single endpoint that replaces the previous 4-5 parallel role/status checks
// run independently by each hub (mobile + web). The backend computes all
// gating rules and returns a declarative response.
export interface EsepCapabilities {
  roles: {
    // True for the old per-wing HR approver AND anyone in the V2 4-level HR
    // roster (any level, either group) — the whole roster gets the same full
    // HR hub, not just level 4.
    is_hr: boolean;
    // True when the caller currently has an active approval step on someone
    // else's resignation chain as a real job-key-hierarchy line manager
    // (AM/Dy.HOD/HOD/Director/MD) — independent of is_hr. An employee can be
    // both an HR-roster member AND a line-manager approver at once; never
    // derive this as "not is_hr".
    is_approver: boolean;
    is_dept_approver: boolean;
    is_finance_approver: boolean;
    is_vigilance_officer: boolean;
    // Registered as a sub-nodal of the vigilance officer.
    is_vigilance_sub_nodal: boolean;
    is_sub_nodal_approver: boolean;
    // V2 only: true specifically for HOD HR (level 4, either group) — gates
    // the independent "Send to CA" action.
    is_hod_hr: boolean;
    // Finance recovery flow: a recovery head (checks a group of No-Due items) and an
    // Establishment manager (verifies the completed checklist). The Finance nodal is
    // is_finance_approver.
    is_recovery_head: boolean;
    is_estt_manager: boolean;
  };
  employee: {
    // True while a termination / death case is live against the employee — every
    // employee-side tab is closed (nothing to view, nothing to download).
    access_locked: boolean;
    access_locked_reason: string | null;
    can_submit_resignation: boolean;
    can_submit_short_notice: boolean;
    can_submit_withdrawal: boolean;
    resignation_block_reason: string | null;
    short_notice_block_reason: string | null;
    withdrawal_block_reason: string | null;
    // May the employee initiate a No-Dues-only case (deputation /
    // retirement / transfer-out)? False while a resignation or another case is live.
    can_initiate_no_dues_case: boolean;
    no_dues_case_block_reason: string | null;
    no_dues_form_enabled: boolean;
    no_dues_form_submitted: boolean;
    no_dues_dispatched: boolean;
    no_dues_request_id: string | null;
    eligible_resignation_id: string | null;
    active_resignation_id: string | null;
  };
}

const DEFAULT_CAPABILITIES: EsepCapabilities = {
  roles: {
    is_hr: false,
    is_approver: false,
    is_dept_approver: false,
    is_finance_approver: false,
    is_vigilance_officer: false,
    is_vigilance_sub_nodal: false,
    is_sub_nodal_approver: false,
    is_hod_hr: false,
    is_recovery_head: false,
    is_estt_manager: false,
  },
  employee: {
    access_locked: false,
    access_locked_reason: null,
    can_submit_resignation: true,
    can_submit_short_notice: true,
    can_submit_withdrawal: true,
    resignation_block_reason: null,
    short_notice_block_reason: null,
    withdrawal_block_reason: null,
    can_initiate_no_dues_case: true,
    no_dues_case_block_reason: null,
    no_dues_form_enabled: false,
    no_dues_form_submitted: false,
    no_dues_dispatched: false,
    no_dues_request_id: null,
    eligible_resignation_id: null,
    active_resignation_id: null,
  },
};

// How many E-Separation cases are waiting on the signed-in user, per role — the red
// count on the sidebar entry. Never throws: any failure just means "none".
export interface EsepPendingCounts {
  total: number;
  approvals: number;
  competent_authority: number;
  hr: number;
  hr_release: number;
  department: number;
  finance_nodal: number;
  recovery_head: number;
  establishment_manager: number;
  vigilance: number;
  // What each HR screen lists for the user (`hr` is their de-duplicated union, and is
  // what `total` counts).
  hr_verification: number;
  hr_final_review: number;
  hr_cases: number;
}
export const EMPTY_ESEP_PENDING_COUNTS: EsepPendingCounts = {
  total: 0,
  approvals: 0,
  competent_authority: 0,
  hr: 0,
  hr_release: 0,
  department: 0,
  finance_nodal: 0,
  recovery_head: 0,
  establishment_manager: 0,
  vigilance: 0,
  hr_verification: 0,
  hr_final_review: 0,
  hr_cases: 0,
};
export const getEsepPendingCounts = async (): Promise<EsepPendingCounts> => {
  try {
    const res = await Api.post(ESEP.pendingCount, {});
    const d: any = unwrapDeep(res);
    if (d && typeof d === "object") return { ...EMPTY_ESEP_PENDING_COUNTS, ...d };
  } catch {
    // fall through
  }
  return EMPTY_ESEP_PENDING_COUNTS;
};
export const getEsepPendingCount = async (): Promise<number> =>
  (await getEsepPendingCounts()).total;

export const getUserCapabilities = async (
  empId?: string
): Promise<EsepCapabilities> => {
  const emp_id = empId || getCurrentEmpNo();
  try {
    const res = await Api.post(ESEP.capabilities, { emp_id });
    const d = unwrapDeep(res);
    if (d && d.roles && d.employee) return d as EsepCapabilities;
  } catch {
    // Fall through to safe default
  }
  return DEFAULT_CAPABILITIES;
};

/* ───────────────────── Final details + documents ──────────────────── */
// Consolidated final view (mirrors the mobile app's getFinalDetails):
//   1. esep/request/find-all { scope: mine } → pick latest request
//   2. esep/request/find-one { request_id }  → full record
//   3. map to the FinalDetails view-model
export const getFinalDetails = async (
  empNo?: string
): Promise<ApiResult<FinalDetails | null>> => {
  const emp_no = empNo || getCurrentEmpNo();
  try {
    const listRes = await Api.post(ESEP.findAll, { scope: "mine", emp_no });
    const allRequests = extractList({ data: unwrap(listRes) });
    // "mine" returns every esep_request row for this employee, including the
    // auto-created NO_DUES sub-request (its own separate row, created AFTER
    // and so ordered before the resignation by id DESC) — exclude it, or
    // `resignations[0]` can silently pick the No-Dues sub-request instead of
    // the actual separation, pointing every download at the wrong request_id
    // (whose own chain has no CA approval, so the stricter Service
    // Certificate / Relieving Order gate fails even though the real
    // resignation is fully approved).
    const resignations = allRequests.filter((r: any) => r.request_type !== "NO_DUES");
    if (!resignations.length) return { success: true, data: null };

    // Pick the latest separation request. A withdrawal that is still pending (or
    // was rejected) is not the case being finalized — the resignation it targets
    // is — so skip it; only an APPROVED withdrawal becomes the final view.
    let picked = resignations[0];
    if (
      picked.request_type === "WITHDRAWAL" &&
      String(picked.status ?? "").toUpperCase() !== "APPROVED"
    ) {
      picked = resignations.find((r: any) => r.request_type !== "WITHDRAWAL") ?? picked;
    }
    const reqId = picked.request_id || picked.id;

    // An approved withdrawal: the resignation is withdrawn and the employee
    // continues in service. No acceptance letter, no certificates — just the
    // Withdrawal Order.
    if (picked.request_type === "WITHDRAWAL") {
      const wdRes = await getResignationDetails(reqId);
      const wd = wdRes.data;
      if (!wd) return { success: true, data: null };
      const origRes = wd.orig_request_id ? await getResignationDetails(wd.orig_request_id) : null;
      const o = origRes?.data || wd;
      return {
        success: true,
        data: {
          emp_no: o.emp_no || emp_no,
          request_id: wd.request_id,
          nodues_request_id: null,
          emp_name: o.emp_name || "",
          designation: o.designation || "",
          department: o.posting || "",
          doj: o.doj || "",
          resignation_type: "Withdrawal of Resignation",
          intimation_date: o.intimation_date || "",
          notice_end_date: o.notice_end_date || "",
          approved_relieving_date: "",
          competent_authority: "",
          short_notice_applied: false,
          bond_status: "N/A",
          no_dues_status: "N/A",
          total_recoverable: 0,
          total_payable: 0,
          net_settlement: 0,
          is_withdrawal: true,
          withdrawal_date: wd.withdrawal_date || "",
          withdrawal_reason: wd.withdrawal_reason || "",
          documents: [
            { doc_type: "withdrawal_order", doc_name: "Withdrawal Order", available: true },
          ],
        } as FinalDetails,
      };
    }
    const detailRes = await getResignationDetails(reqId);
    const data = detailRes.data;
    if (!data) return { success: true, data: null };

    const steps: any[] = data.steps ?? [];
    const ca = steps.find((s) => s.is_ca);
    const status = String(data.status ?? "").toUpperCase();
    const cleared = status === "APPROVED";
    // Acceptance Letter: available once the Competent Authority has approved
    // (CA_APPROVED or later) — mirrors the backend's generate-acceptance-pdf
    // gate. Unchanged by workflow_version: processCaAction (V2) stamps the
    // same CA_APPROVED status regardless of HR-chain progress.
    const caApproved = status === "CA_APPROVED" || status === "APPROVED";
    const isV2Workflow = String(data.workflow_version ?? "V1").toUpperCase() === "V2";
    // Whether every HR level has approved — for V1 this is the single routing
    // HR step; for V2 all 4 levels. Prefer the backend's explicit
    // hr_chain_complete (generalizes both) when present.
    const hrAdminStep = steps.find((s) => s.is_hr);
    const hrAdminApproved =
      typeof data.hr_chain_complete === "boolean"
        ? data.hr_chain_complete
        : String(hrAdminStep?.status ?? "").toUpperCase() === "APPROVED";
    const payable = parseFloat(data.payable_amount) || 0;

    // The No-Dues Settlement report is generated against the dedicated ND-…
    // request, not the resignation's own request_id. Fetched independently of
    // hrAdminApproved for V2, since No-Dues runs in parallel with the HR chain
    // rather than after it.
    let nodues_request_id: string | null = null;
    // A No-Dues-only case (no resignation / approval flow) is complete the moment
    // its No-Dues is settled — there is no HR chain or Competent Authority. (Old V1
    // death cases still treat No-Dues as auto-cleared.)
    const requestType = String(data.request_type ?? "").toUpperCase();
    const isNoDuesOnly = ESEP_NO_DUES_ONLY_TYPES.includes(requestType) && isV2Workflow;
    let noDuesSettled = requestType === "DEATH" && !isNoDuesOnly;
    if (hrAdminApproved || isV2Workflow) {
      const ndRes = await getNoDuesFormDetails(reqId);
      nodues_request_id = ndRes.data?.nodues_request_id || null;
      if (!noDuesSettled && nodues_request_id) {
        const ndDetail = await getResignationDetails(nodues_request_id);
        noDuesSettled = String(ndDetail.data?.status ?? "").toUpperCase() === "APPROVED";
      }
    }
    // Service Certificate / Relieving Order / Final Settlement readiness: for
    // V1, unchanged (hrAdminApproved alone); for V2, the real 3-condition AND
    // (mirrors the backend's canGenerateFinalDocs).
    const canGenerateFinalDocs = isNoDuesOnly
      ? noDuesSettled
      : isV2Workflow
      ? hrAdminApproved && caApproved && noDuesSettled
      : hrAdminApproved;

    const final: FinalDetails = {
      emp_no: data.emp_no || emp_no,
      request_id: data.request_id,
      nodues_request_id,
      office_order_no: data.office_order_no || null,
      docs_pending_release: canGenerateFinalDocs && !data.office_order_no,
      emp_name: data.emp_name || "",
      designation: data.designation || "",
      department: data.posting || "",
      doj: data.doj || "",
      resignation_type: data.request_type || data.separation_type || "Resignation",
      intimation_date: data.intimation_date || "",
      notice_end_date: data.notice_end_date || "",
      approved_relieving_date:
        data.approved_wef || data.desired_relieve_date || data.requested_relieve_date || "",
      competent_authority: ca?.approver_empId || "Competent Authority",
      short_notice_applied: (data.shortfall_days || 0) > 0,
      shortfall_days: data.shortfall_days || 0,
      short_notice_deducted: payable,
      bond_status: data.is_bond === "Y" || data.is_bond === true ? "Active" : "N/A",
      no_dues_status: cleared ? "Cleared" : "Pending",
      total_recoverable: payable,
      total_payable: 0,
      net_settlement: 0 - payable,
      documents: [
        // No acceptance letter for a No-Dues-only case (no resignation to accept).
        ...(isNoDuesOnly
          ? []
          : [{ doc_type: "acceptance_letter", doc_name: "Acceptance Letter", available: caApproved }]),
        // Both stay locked until the HR Assistant gives the final release.
        { doc_type: "service_certificate", doc_name: "Service Certificate", available: canGenerateFinalDocs && !!data.office_order_no },
        { doc_type: "relieving_letter", doc_name: "Relieving Order", available: canGenerateFinalDocs && !!data.office_order_no },
        {
          doc_type: "no_dues_certificate_doc",
          doc_name: "No Dues Certificate",
          available: !!nodues_request_id,
        },
      ],
    };
    return { success: true, data: final };
  } catch (e: any) {
    return { success: true, data: null, message: Api.getErrorMessage(e) };
  }
};

// Document generation/download — no PDF endpoints in esep/ v2 except biodata.
export const downloadDocument = async (
  docType: string,
  id: string
): Promise<ApiResult<any>> => {
  if (docType === "biodata" || docType === "profile") {
    return downloadBiodataPDF(id);
  }
  return {
    success: false,
    message: "Document generation is not available in the e-separation v2 flow.",
  };
};

// Fetches the acknowledgment PDF and returns a blob object-URL (for the
// preview iframe / download link in EseprationOverview.tsx) rather than
// triggering a click-download itself — the caller owns the URL's lifecycle
// and revokes it when the preview closes.
export const fetchAcknowledgmentPdf = async (
  requestId: string
): Promise<ApiResult<string>> => {
  try {
    const token: string | null = tokenService.getToken();
    const guid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const endpointPath = "esep/request/acknowledgement/pdf";
    const body = {
      Header: {
        device_id: "WEB",
        device_token: "WEB",
        device_type: "WEB",
        guid,
        epoch: Date.now(),
        url: endpointPath,
        service_request_id: endpointPath,
      },
      Request: { data: { request_id: requestId } },
    };
    const resp = await axios({
      method: "POST",
      url: ESEP_ACKNOWLEDGEMENT_PDF,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        "enc-req": 0,
        "enc-res": 0,
      },
      data: body,
      responseType: "arraybuffer",
    });
    const bytes = resp?.data;
    if (!bytes || (bytes.byteLength ?? 0) === 0) {
      return { success: false, data: "", message: "Empty PDF received from server." };
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const objUrl = window.URL.createObjectURL(blob);
    return { success: true, data: objUrl };
  } catch (e: any) {
    return { success: false, data: "", message: Api.getErrorMessage(e) };
  }
};

export const uploadDocument = (formData: any): Promise<ApiResult<any>> =>
  withFallback(() => Api.post(AWS_UPLOAD, formData), null);

/* ────────────────────── Approvals (single endpoint) ────────────────── */
// Approval History — built from the append-only v2 ledger returned by find-one.
export const getApprovalHistory = async (
  requestId: string
): Promise<ApiResult<ApprovalStep[]>> => {
  try {
    const detail = await getResignationDetails(requestId);
    const history: any[] = detail?.data?.history ?? [];
    const statusMap: Record<string, ApprovalStep["status"]> = {
      APPROVE: "Approved",
      SUBMIT: "Approved",
      RESUBMIT: "Approved",
      AUTO_FORWARD: "Approved",
      REJECT: "Rejected",
      SEND_BACK: "Pending",
      CLARIFY: "Clarification",
      DISPATCH: "Dispatched",
      DEPT_APPROVE: "Approved",
      DEPT_CLEAR: "Approved",
      DEPT_REJECT: "DuesFound",
      SETTLE_DISPATCH: "Settled",
      SETTLE_ITEM: "Collected",
      SETTLE_CLOSE: "Collected",
    };
    const steps = history.map((h: any) => {
      const approver =
        h.approver_name || h.approver_empId || h.approver_emp_id || "—";
      return {
        level: h.role || `Level ${h.level_no ?? ""}`.trim(),
        approver_name: approver,
        approver_emp_no: h.approver_empId || h.approver_emp_id || "",
        status: statusMap[String(h.action ?? "").toUpperCase()] || "Pending",
        remarks: h.comments || "",
        action_date: h.acted_at,
        action: String(h.action ?? "").toUpperCase(),
      };
    });
    return { success: true, data: steps };
  } catch (e: any) {
    return { success: true, data: [], message: Api.getErrorMessage(e) };
  }
};

/** Days elapsed since the intimation/submission date (min 0). */
const daysSince = (dateStr?: string): number => {
  if (!dateStr) return 0;
  const then = new Date(dateStr).getTime();
  if (isNaN(then)) return 0;
  const diffDays = Math.ceil(Math.abs(Date.now() - then) / DAY_MS) - 1;
  return diffDays >= 0 ? diffDays : 0;
};

/** Map one raw v2 request record → PendingApprovalItem. */
const mapToPendingApproval = (r: any): PendingApprovalItem => {
  const type: SeparationType = requestTypeToLabel(
    r?.request_type || r?.separation_type
  );
  return {
    request_id: r?.request_id || r?.id || "—",
    type,
    emp_name: r?.emp_name || "Employee",
    emp_no: r?.emp_no || "",
    designation: r?.designation || "",
    department: r?.posting || "",
    submitted_date: r?.intimation_date || r?.created_at || "",
    days_pending: daysSince(r?.intimation_date || r?.created_at),
    current_level: r?.current_step_role || r?.next_approver_role || "Approver",
    priority:
      daysSince(r?.intimation_date || r?.created_at) > 5
        ? "Overdue"
        : daysSince(r?.intimation_date || r?.created_at) > 2
        ? "Urgent"
        : "Normal",
    status: r?.status || "PENDING_APPROVAL",
    request_type: r?.request_type || "",
    current_step_is_hr: !!r?.current_step_is_hr,
    current_step_role: r?.current_step_role || "",
  };
};

// Approver / HR inbox — sourced from esep/request/find-all { scope: inbox },
// merged with esep/ca/inbox (the parallel Competent Authority track, which
// carries no esep_approval_step row so it would otherwise never appear here)
// so every pending decision — step-based or CA — shows up in the same single
// "Pending Approvals" list, with is_ca_item distinguishing how to act on it.
// Written by hand (not via withFallback) since that helper unwraps a single
// response; this merges two.
export const getPendingApprovals = async (
  _role?: string,
  _empNo?: string
): Promise<ApiResult<PendingApprovalItem[]>> => {
  try {
    const [stepRes, caRes] = await Promise.all([
      Api.post(ESEP.findAll, { scope: "inbox" }),
      Api.post(ESEP.caInbox, {}),
    ]);
    const stepItems = extractList({ data: unwrap(stepRes) }).map(mapToPendingApproval);
    const caItems = extractList({ data: unwrap(caRes) }).map((r: any) => ({
      ...mapToPendingApproval(r),
      current_level: "Competent Authority",
      current_step_role: "CA",
      current_step_is_hr: false,
      is_ca_item: true,
    }));
    return { success: true, data: [...stepItems, ...caItems] };
  } catch (e: any) {
    return { success: true, data: [], message: Api.getErrorMessage(e) };
  }
};

// Processed Approvals — requests this approver has already acted on.
export const getProcessedApprovals = (
  _role?: string,
  _empNo?: string
): Promise<ApiResult<PendingApprovalItem[]>> =>
  withFallback(
    () => Api.post(ESEP.findAll, { scope: "processed" }),
    [],
    (p) =>
      extractList({ data: p }).map((r: any) => ({
        ...mapToPendingApproval(r),
        priority: "Normal" as const,
        my_action: r?.my_action || "",
        status: r?.status || "APPROVED",
      }))
  );

// A resignation on the HR approver's desk for the post-CA Final Review list.
export interface HrFinalReviewRow {
  request_id: string; // the resignation RES-… (acted upon + opens detail)
  emp_no: string;
  emp_name: string;
  designation: string;
  status: string; // PENDING_APPROVAL | CA_APPROVED | APPROVED | REJECTED | SENT_BACK
  // The final HR step's own decision, if recorded (APPROVE | REJECT | SEND_BACK),
  // else ''. Drives Pending vs Processed — status alone can be CA_APPROVED while
  // still awaiting HR, or REJECTED by the CA without HR having acted.
  hr_action: string;
}

// HR Final Review list — resignations forwarded to the CA by the calling HR user.
export const getHrFinalReviewList = async (): Promise<HrFinalReviewRow[]> => {
  try {
    const res = await Api.post(ESEP.hrFinalReviewList, {});
    const arr = extractList({ data: unwrap(res) });
    return arr.map((r: any) => ({
      request_id: r.request_id || "",
      emp_no: r.emp_no || "",
      emp_name: r.emp_name || "",
      designation: r.designation || "—",
      status: String(r.status || "").toUpperCase(),
      hr_action: String(r.hr_action || "").toUpperCase(),
    }));
  } catch (err) {
    console.log("getHrFinalReviewList error:", err);
    return [];
  }
};

// Single action-driven approval endpoint for approve / reject / send-back / clarify.
export const submitApprovalAction = (
  data: ApprovalAction & { comments?: string; forwardToCA?: boolean }
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    postKeepingAction(ESEP.approval, {
      request_id: data.request_id,
      action: normalizeAction(data.action),
      comments: (data as any).remarks || data.comments || "",
    })
  );

/* Role-specific reviews all funnel into the single v2 approval endpoint. The
 * dynamic chain decides who the current approver is; the wrappers are kept so
 * existing pages keep compiling. */
type ReviewVerb = "APPROVE" | "REJECT";

export const submitDeputyReview = (data: {
  request_id: string;
  action: ReviewVerb;
  dar_remarks: string;
  approver_id?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    postKeepingAction(ESEP.approval, {
      request_id: data.request_id,
      action: normalizeAction(data.action),
      comments: data.dar_remarks || "",
    })
  );

export const submitControllingReview = (data: {
  request_id: string;
  action: ReviewVerb;
  remarks: string;
  approver_id?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    postKeepingAction(ESEP.approval, {
      request_id: data.request_id,
      action: normalizeAction(data.action),
      comments: data.remarks || "",
    })
  );

export const submitHODReview = (data: {
  request_id: string;
  action: ReviewVerb;
  dar_remarks: string;
  approver_id?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    postKeepingAction(ESEP.approval, {
      request_id: data.request_id,
      action: normalizeAction(data.action),
      comments: data.dar_remarks || "",
    })
  );

export const submitDirectorFinalize = (data: {
  request_id: string;
  action: ReviewVerb;
  remarks: string;
  bond_waiver_approved?: boolean;
  waived_amount?: number;
  approver_id?: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    postKeepingAction(ESEP.approval, {
      request_id: data.request_id,
      action: normalizeAction(data.action),
      comments: data.remarks || "",
    })
  );

/**
 * Route an approval action — all roles collapse to esep/approval/action in v2.
 * `dar_details` is forwarded to the backend for AM / DY_HOD approvers so they
 * can save D&AR information alongside their approval action in a single call.
 */
export const submitRoleApproval = (params: {
  role: string;
  request_id: string;
  action: ApprovalActionType;
  remarks: string;
  forwardToCA?: boolean;
  dar_details?: DarDetails | null;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    postKeepingAction(ESEP.approval, {
      request_id: params.request_id,
      action: normalizeAction(params.action),
      comments: params.remarks || "",
      ...(params.dar_details ? { dar_details: params.dar_details } : {}),
    })
  );

// Raising a clarification is the CLARIFY action on the single approval endpoint.
export const raiseClarification = (data: {
  request_id: string;
  query_string: string;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    postKeepingAction(ESEP.approval, {
      request_id: data.request_id,
      action: "CLARIFY",
      comments: data.query_string,
    })
  );

// Employee responds to a raised clarification.
export const respondClarification = (data: {
  request_id: string;
  clarification_id: number;
  response_text: string;
  // Additional documents the approver / HR asked for (already uploaded to S3).
  documents?: { doc_name: string; s3_key: string; file_name: string }[];
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.clarificationRespond, {
      clarification_id: data.clarification_id,
      response: data.response_text,
      ...(data.documents?.length ? { documents: data.documents } : {}),
    })
  );

// DAR Clearance Remarks — surfaced from the approval-history ledger.
export const getDARClearanceRemarks = async (
  requestId: string
): Promise<ApiResult<DARRemarks[]>> => {
  const histRes = await getApprovalHistory(requestId);
  // The remark summary is each approver's DECISION remark only. Clarification
  // messages (questions and the employee's replies) have their own Clarifications
  // section and must never be mixed in here.
  const DECISION_ACTIONS = ["APPROVE", "REJECT", "SEND_BACK"];
  const remarks = (histRes.data || [])
    .filter((h) => h.remarks && DECISION_ACTIONS.includes(String(h.action ?? "")))
    .map((h) => ({
      level: h.level,
      officer_name: h.approver_name,
      officer_emp_no: h.approver_emp_no,
      remarks: h.remarks || "",
      dar_status: (h.status === "Rejected" ? "Hold" : "Cleared") as DARRemarks["dar_status"],
      action_date: h.action_date || "",
    }));
  return { success: true, data: remarks };
};

// Formal acceptance of resignation letter — gated to APPROVED status by the
// backend. Downloads as a PDF blob.
export const generateAcceptanceLetter = async (
  requestId: string
): Promise<ApiResult<any>> => {
  try {
    const guid = `ESEP-ACCEPT-${Date.now()}`;
    const token = tokenService.getToken();
    const body = {
      Header: {
        device_id: "WEB",
        device_token: "WEB",
        device_type: "WEB",
        guid,
        epoch: Date.now(),
        url: "esep/request/generate-acceptance-pdf",
        service_request_id: "esep/request/generate-acceptance-pdf",
      },
      Request: { data: { request_id: requestId } },
    };
    const resp = await axios({
      method: "POST",
      url: ESEP_ACCEPTANCE_PDF,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        "enc-req": 0,
        "enc-res": 0,
      },
      data: body,
      responseType: "arraybuffer",
    });
    const bytes = resp?.data;
    if (!bytes || (bytes.byteLength ?? 0) === 0) {
      return { success: false, message: "Empty PDF received from server." };
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = `Acceptance_Letter_${requestId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objUrl);
    return { success: true, data: null };
  } catch (e: any) {
    const msg: string =
      e?.response?.data?.message ||
      e?.response?.statusText ||
      e?.message ||
      "Failed to generate acceptance letter.";
    return { success: false, message: msg };
  }
};

// Generic PDF-blob download for the E-Separation document endpoints — same
// request/response shape as generateAcceptanceLetter above, parameterised by
// URL and filename so Service Certificate / Relieving Order don't repeat it.
const downloadEsepPdf = async (
  url: string,
  requestId: string,
  filenamePrefix: string,
  failureMessage: string
): Promise<ApiResult<any>> => {
  try {
    const guid = `ESEP-${filenamePrefix.toUpperCase()}-${Date.now()}`;
    const token = tokenService.getToken();
    const body = {
      Header: {
        device_id: "WEB",
        device_token: "WEB",
        device_type: "WEB",
        guid,
        epoch: Date.now(),
        url,
        service_request_id: url,
      },
      Request: { data: { request_id: requestId } },
    };
    const resp = await axios({
      method: "POST",
      url,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        "enc-req": 0,
        "enc-res": 0,
      },
      data: body,
      responseType: "arraybuffer",
    });
    const bytes = resp?.data;
    if (!bytes || (bytes.byteLength ?? 0) === 0) {
      return { success: false, message: "Empty PDF received from server." };
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = `${filenamePrefix}_${requestId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objUrl);
    return { success: true, data: null };
  } catch (e: any) {
    const msg: string =
      e?.response?.data?.message || e?.response?.statusText || e?.message || failureMessage;
    return { success: false, message: msg };
  }
};

// Withdrawal Order — the letter issued once a resignation withdrawal is approved.
// Accepts the withdrawal request id (or the original resignation's id).
export const generateWithdrawalOrder = (requestId: string): Promise<ApiResult<any>> =>
  downloadEsepPdf(
    ESEP.generateWithdrawalOrderPdf,
    requestId,
    "Withdrawal_Order",
    "Failed to generate the Withdrawal Order."
  );

// HR Assistant's final release of the Relieving Order and Service Certificate.
// The office order number typed here is printed on the Relieving Order
// ("OFFICE ORDER No. {wing}/HR- {number} of {year}"); both documents stay locked
// until this is done.
export const releaseFinalDocs = (
  requestId: string,
  officeOrderNo: string
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP.releaseFinalDocs, {
      request_id: requestId,
      office_order_no: officeOrderNo.trim(),
    })
  );

// The HR Assistant changes the signing authority and / or the office order number of
// the final documents (any time, before or after the release).
export const updateFinalDocs = (data: {
  request_id: string;
  office_order_no?: string;
  signatory_emp_id?: string;
  signatory_name?: string;
  signatory_designation?: string;
  clear_signatory?: boolean;
}): Promise<ApiResult<any>> =>
  withFallbackStrict(() => Api.post(ESEP.updateFinalDocs, data));

// The wing as written in the office order number: maintenance + operations read
// "O&M", the project wing reads "Project".
export const officeOrderWingLabel = (wing?: string | null): string => {
  const w = String(wing ?? "").trim().toUpperCase();
  if (!w) return "O&M";
  if (w.startsWith("PROJ")) return "Project";
  if (w === "MNT" || w === "OPS" || w === "O&M" || w === "OM") return "O&M";
  return w;
};

// Service Certificate — downloadable once HR Admin approves the request.
export const generateServiceCertificate = (requestId: string): Promise<ApiResult<any>> =>
  downloadEsepPdf(
    ESEP.generateServiceCertificatePdf,
    requestId,
    "Service_Certificate",
    "Failed to generate Service Certificate."
  );

// Relieving Order — downloadable once HR Admin approves the request.
export const generateRelievingOrder = (requestId: string): Promise<ApiResult<any>> =>
  downloadEsepPdf(
    ESEP.generateRelievingOrderPdf,
    requestId,
    "Relieving_Order",
    "Failed to generate Relieving Order."
  );

// No Dues Certificate — downloadable once every dispatched department on the
// linked No-Dues request has cleared.
export const generateNoDuesCertificate = (requestId: string): Promise<ApiResult<any>> =>
  downloadEsepPdf(
    ESEP.generateNoDuesCertificatePdf,
    requestId,
    "No_Dues_Certificate",
    "Failed to generate No Dues Certificate."
  );

/* ──────────────── Finance / Nodal (legacy stubs) ────────────── */
// No-Dues officer checklist workflow is deferred in v2 (the dispatch flow
// replaces it). Stubbed so the legacy Nodal/Finance pages keep importing.
export const getNoDuesTasks = (
  _nodalEmpNo?: string
): Promise<ApiResult<NoDuesTask[]>> =>
  Promise.resolve({ success: true, data: [] });

export const submitNoDuesAction = (_data: {
  task_id: string;
  action: string;
  checklist: NoDuesChecklistItem[];
  recovery_amount: number;
  remarks: string;
  attachments?: string[];
}): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: false,
    message: "No-Dues checklist workflow is deferred in the e-separation v2 flow.",
  });

export const getFinanceLoans = (
  empNo?: string
): Promise<ApiResult<FinanceLoanItem[]>> =>
  withFallback(
    async () => {
      const listRes = await Api.post(ESEP.findAll, {
        scope: "mine",
        emp_no: empNo || getCurrentEmpNo(),
      });
      const resignations = extractList({ data: unwrap(listRes) });
      if (!resignations.length) return [];
      const detail = await getResignationDetails(resignations[0].request_id);
      const loans: any[] = detail?.data?.loans ?? [];
      return loans.map((l: any) => ({
        loan_type: l.loan_type,
        sanctioned_amount: parseFloat(l.amount) || 0,
        outstanding_amount: parseFloat(l.amount) || 0,
        recovery_required: parseFloat(l.amount) || 0,
        status: "Active" as const,
        remarks: "",
      }));
    },
    [],
    (p) => (Array.isArray(p) ? p : [])
  );

export const submitFinanceClearance = (_data: {
  nd_case_id: string;
  emp_no: string;
  loans: FinanceLoanItem[];
  total_recovery: number;
  remarks: string;
  action: string;
}): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: false,
    message: "No-Dues finance clearance is deferred in the e-separation v2 flow.",
  });

// Manual No-Dues initiation is deferred in v2 (only enable + submit exist).
export const initiateNoDues = (
  _data: NoDuesInitiateData
): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: false,
    message: "Manual No-Dues initiation is deferred in the e-separation v2 flow.",
  });

export const submitNoDuesFinalReview = (_data: {
  nd_case_id: string;
  action: "Approved" | "Rejected";
  remarks: string;
}): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: false,
    message: "HR No-Dues final review is deferred in the e-separation v2 flow.",
  });

// Prepone / postpone of relieving date have no v2 endpoint.
export const preponeResignation = (_data: {
  request_id: string;
  new_relieving_date: string;
  remarks: string;
}): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: false,
    message: "Prepone is not available in the e-separation v2 flow.",
  });

export const postponeResignation = (_data: {
  request_id: string;
  new_relieving_date: string;
  remarks: string;
}): Promise<ApiResult<any>> =>
  Promise.resolve({
    success: false,
    message: "Postpone is not available in the e-separation v2 flow.",
  });

/* ───────────────────────── Profile / Biodata ───────────────────────── */
// E-Separation v2 structured biodata (father/mother, addresses, qualifications,
// experience, penalties, …) sourced from the real HR tables.
export const getEmployeeProfile = (
  empNo?: string
): Promise<ApiResult<EmployeeProfile | null>> =>
  withFallback(
    () =>
      Api.post(ESEP_PROFILE, {
        emp_no: empNo || getCurrentEmpNo(),
      }),
    null
  );

/**
 * HR Admin — fetch ANY employee's biodata by emp_no (HR-gated on the backend).
 * Distinct from `getEmployeeProfile`, which always returns the caller's own
 * record regardless of any emp_no passed to it.
 */
export const getHrEmployeeProfile = (
  empNo: string
): Promise<ApiResult<EmployeeProfile | null>> =>
  withFallback(
    () => Api.post(ESEP_HR_BIODATA_PROFILE, { emp_no: empNo }),
    null
  );

/**
 * HR Admin — update (override) an employee's biodata.
 * The backend merges only the scalar personal/service fields; section arrays
 * (qualifications, employers …) are left untouched.
 */
export const updateBiodataAsHR = (
  profile: Partial<EmployeeProfile> & { emp_no?: string }
): Promise<ApiResult<any>> =>
  withFallbackStrict(() =>
    Api.post(ESEP_HR_BIODATA_UPDATE, {
      emp_no: profile.emp_no || getCurrentEmpNo(),
      father_name: profile.father_name,
      mother_name: profile.mother_name,
      dob: profile.dob,
      gender: profile.gender,
      marital_status: profile.marital_status,
      blood_group: profile.blood_group,
      category: profile.category,
      nationality: profile.nationality,
      religion: profile.religion,
      pwd_status: profile.pwd_status,
      mobile: profile.mobile,
      email: profile.email,
      present_address: profile.present_address,
      permanent_address: profile.permanent_address,
      pay_scale: profile.pay_scale,
      doj: profile.doj,
      status_of_appointment: profile.status_of_appointment,
      emp_type: profile.emp_type,
    })
  );

// Biodata PDF — the endpoint streams raw bytes, so this bypasses the JSON
// wrapper and downloads the blob directly in the browser.
async function downloadBiodataPdfFrom(
  url: string,
  endpointPath: string,
  emp_no: string
): Promise<ApiResult<any>> {
  try {
    const token: string | null = tokenService.getToken();
    const guid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const body = {
      Header: {
        device_id: "WEB",
        device_token: "WEB",
        device_type: "WEB",
        guid,
        epoch: Date.now(),
        url: endpointPath,
        service_request_id: endpointPath,
      },
      Request: { data: { emp_no } },
    };
    const resp = await axios({
      method: "POST",
      url,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        "enc-req": 0,
        "enc-res": 0,
      },
      data: body,
      responseType: "arraybuffer",
    });
    const bytes = resp?.data;
    if (!bytes || (bytes.byteLength ?? 0) === 0) {
      return { success: false, message: "Empty PDF received from server." };
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const objUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = `Biodata_Profile_${emp_no}_${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objUrl);
    return { success: true };
  } catch (e: any) {
    return { success: false, message: Api.getErrorMessage(e) };
  }
}

// Employee (self) — downloads the caller's own biodata PDF.
export const downloadBiodataPDF = (empNo?: string): Promise<ApiResult<any>> =>
  downloadBiodataPdfFrom(ESEP_BIODATA_PDF, "esep/me/biodata/pdf", empNo || getCurrentEmpNo());

// HR Admin — downloads the biodata PDF for ANY employee by emp_no.
export const downloadHrBiodataPDF = (empNo: string): Promise<ApiResult<any>> =>
  downloadBiodataPdfFrom(ESEP_HR_BIODATA_PDF, "esep/hr/biodata/pdf", empNo);

export const ND_DEPARTMENT_NAMES = ND_DEPARTMENTS;
export const emptyFinance = emptyFinanceSummary;
